// src/pages/draft_document/DraftDocumentNew.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";  // ← 최상단 import에 추가

import styles from "./DraftDocumentNew.module.css";
import api from "../../api";
import { useTabActivated } from "../../tabs/TabActivation";
import { formatDraftSaveError } from "../../utils/draftNumberNotice";

import ApprovalLine from "../../components/approval-line/ApprovalLine";
import ApprovalLineModal from "../../components/approval-line/ApprovalLineModal";
import ApprovalLineConfirmDialog from "../../components/approval-line/ApprovalLineConfirmDialog";
import { EXPENSE_TEMPLATES } from "../../components/preview/expense/expenseTemplates";
import DocumentPreview from "./DocumentPreview";  // ✅ 리팩터링 1단계 — 미리보기 분리
import TemplateFormFields from "./TemplateFormFields";  // ✅ 리팩터링 2단계 — 입력 폼 분리


import DocumentTypeSelector from "../../components/document-type/DocumentTypeSelector";
import { VENDOR_CONFIG } from "../../components/document-type/ServiceGiseongAutoFill";



const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };
const CODE_TO_PAY_METHOD = { transfer: "계좌이체", card: "카드결제", cash: "현금결제" };

// ✅ amount(금액)를 표 합계로 자동 계산하는 템플릿 목록 — 이 목록에 없는 템플릿으로 전환하면
// DB에서 불러온 원본 금액(loadedAmount)을 복원한다.
export const AUTO_SUM_TEMPLATES = [
  "interview_fee",
  "deduction_refund",
  "pension",
  "withholding_tax",
  "water_rental",
  "server_fee",
  "service_purchase",
  "service_daily_wage",
  "gasan_pos_rental",
  "gasan_social_insurance",
  // ✅ 카페인 독산점 신규(표 3종) — withholding_tax는 doksan_withholding_tax가 기존 로직을 공유
  "doksan_withholding_tax",
  "doksan_holiday_gift",
  "doksan_vending_terminal",
  // ✅ 사회보험료 자동합계 — doksan은 이전 작업에서 등록이 누락되어 있던 것을 금나래 작업 중 발견,
  // geumnarae 추가와 함께 보강
  "doksan_social_insurance",
  "geumnarae_social_insurance",
  // ✅ 포스임대료 자동합계 — doksan도 동일하게 누락되어 있던 것을 함께 보강
  "doksan_pos_rental",
  "geumnarae_pos_rental",
  // ✅ 카페인 금나래점 신규(원천세) — doksan_withholding_tax와 로직 공유
  "geumnarae_withholding_tax",
  "geumnarae_holiday_gift",
  // ✅ 카페인 그린점 신규
  "green_pos_rental",
  "green_social_insurance",
  "green_holiday_gift",
  "green_withholding_tax",
  "green_vending_terminal",
];

// ✅ 카페인 가산점 전용 — 귀속연월(gasanMonth) 입력 Row가 필요한 템플릿 목록 (포스임대료는 귀속연월 없음)
export const GASAN_MONTH_TEMPLATES = [
  "gasan_ingredient",
  "gasan_goods",
  "gasan_supplies",
  "gasan_internet",
  "gasan_credit_card",
  "gasan_payroll",
  "gasan_social_insurance",
  "gasan_maintenance",
  // ✅ 카페인 독산점 — 가산점과 동일 규칙(포스임대료 제외)
  "doksan_ingredient",
  "doksan_goods",
  "doksan_internet",
  "doksan_credit_card",
  "doksan_payroll",
  "doksan_social_insurance",
  // ✅ 카페인 독산점 공과금 3종 — 귀속연월 있음(수도/가스/전기)
  "doksan_water",
  "doksan_gas",
  "doksan_electric",
  // ✅ 카페인 금나래점 — 가산점과 동일 규칙(포스임대료 제외)
  "geumnarae_ingredient",
  "geumnarae_goods",
  "geumnarae_supplies",
  "geumnarae_payroll",
  "geumnarae_social_insurance",
  "geumnarae_credit_card",
  "geumnarae_internet",
  "geumnarae_water",
  "geumnarae_gas",
  "geumnarae_electric",
  // ✅ 카페인 그린점 — 금나래점과 동일 규칙(포스임대료 제외)
  "green_water",
  "green_gas",
  "green_electric",
  "green_ingredient",
  "green_goods",
  "green_supplies",
  "green_internet",
  "green_credit_card",
  "green_payroll",
  "green_social_insurance",
  "green_maintenance",
];



// 📂 문서 종류 + 카테고리별 백엔드 API 엔드포인트
const CATEGORY_ENDPOINTS = {
  income_draft: {
    "경영팀": "/income/business/",
    "용역사업": "/income-service/",
    "환경/소독": "/income-drafts/",
    "환경/종합": "/env-total-incomes/",
  },
  expense_draft: {
    "경영팀": "/expense/business/",
    "용역사업": "/expense/service/",
    "환경/소독": "/expense/env/disinfection/",
    "환경/종합": "/expense/env/total/",
    "CAFE_INN_가산": "/expense/cafe/gasan/",
    "CAFE_INN_독산": "/expense/cafe/doksan/",
    "CAFE_INN_금나래": "/expense/cafe/geumnarae/",
    "CAFE_INN_그린": "/expense/cafe/green/",
    "테스트": "/expense/test/",
  },
  income_resolution: {
    "경영팀": "/income/business/",
    "용역사업": "/income-service/",
    "환경/소독": "/income-drafts/",
    "환경/종합": "/env-total-incomes/",
  },
  expense_resolution: {
    "경영팀": "/expense/business/",
    "용역사업": "/expense/service/",
    "환경/소독": "/expense/env/disinfection/",
    "환경/종합": "/expense/env/total/",
  },
};

// 📂 문서 종류별 사용 가능한 카테고리
const CATEGORY_OPTIONS = {
  income_draft: ["경영팀", "용역사업", "환경/소독", "환경/종합"],
  expense_draft: ["경영팀", "용역사업", "환경/소독", "환경/종합", "CAFE_INN_가산", "CAFE_INN_독산", "CAFE_INN_금나래", "CAFE_INN_그린", "테스트"],
  income_resolution: ["경영팀", "용역사업", "환경/소독", "환경/종합"],
  expense_resolution: ["경영팀", "용역사업", "환경/소독", "환경/종합"],
};

// ✅ 추가: 원본 데이터 열기 버튼용 — 백엔드 API 엔드포인트 → 프론트 목록 페이지 경로
export const SOURCE_LIST_ROUTES = {
  "/income/business/": "/income/business/list",
  "/income-service/": "/income/service/list",
  "/income-drafts/": "/income/env/disinfection/list",
  "/env-total-incomes/": "/income/env/total/list",
  "/expense/business/": "/expense/business/list",
  "/expense/service/": "/expense/service/list",
  "/expense/env/disinfection/": "/expense/env/disinfection/list",
  "/expense/env/total/": "/expense/env/total/list",
  "/expense/cafe/gasan/": "/expense/cafe/gasan/list",
  "/expense/cafe/doksan/": "/expense/cafe/doksan/list",
  "/expense/cafe/geumnarae/": "/expense/cafe/geumnarae/list",
  "/expense/cafe/green/": "/expense/cafe/green/list",
  "/expense/test/": "/expense/test/list",
};

// ✅ 자료 목록 항목의 금액 계산 — 카테고리별로 API가 노출하는 금액 필드가 다르다
// (경영팀/환경종합/카페 4점은 amount 또는 total_amount가 응답에 포함되지만,
//  용역사업/환경소독은 현재 total_amount가 API 응답에서 빠져 있어 0으로 떨어짐 —
//  supply_amount+vat로 최종 폴백해 어떤 카테고리든 0원으로 보이지 않게 한다)
function resolveItemAmount(item) {
  return (
    Number(item.amount) ||
    Number(item.total_amount) ||
    Number(item.supply_amount || 0) + Number(item.vat || 0) ||
    0
  );
}

// ✅ 추가: endpoint → app 이름 변환
// ✅ CATEGORY_ENDPOINTS의 엔드포인트 하나당 값 하나씩 대응 — 서버(draft_document/serializers.py
// SOURCE_APP_MODEL_MAP)의 키와 반드시 일치해야 한다. 이전엔 "/expense/"로 시작하는 모든 카테고리가
// 전부 "expense" 하나로 뭉뚱그려져 있어(가산/독산/금나래/그린/용역/경영팀/환경 등 구분 불가),
// 서버가 어떤 모델을 갱신해야 할지 알 수 없었던 문제를 함께 해결.
const ENDPOINT_TO_SOURCE_APP = {
  "/income/business/": "income_business",
  "/income-service/": "income_service",
  "/income-drafts/": "income",
  "/env-total-incomes/": "income_env_total",
  "/expense/business/": "expense_business",
  "/expense/service/": "expense_service",
  "/expense/env/disinfection/": "expense_env_disinfection",
  "/expense/env/total/": "expense_env_total",
  "/expense/cafe/gasan/": "expense_cafe_gasan",
  "/expense/cafe/doksan/": "expense_cafe_doksan",
  "/expense/cafe/geumnarae/": "expense_cafe_geumnarae",
  "/expense/cafe/green/": "expense_cafe_green",
  "/expense/test/": "expense_test",
};

function endpointToApp(endpoint) {
  if (!endpoint) return "";
  return ENDPOINT_TO_SOURCE_APP[endpoint] || "";
}

// ✅ 수입기안 채번(draftNo) 전용 카테고리 표시명 — "/"가 포함된 카테고리명을 그대로 쓰면
// 기안번호에 "/"가 섞여 보이므로(예: 금천일자리(주)환경/소독-...) 짧은 표시명으로 교체
const INCOME_DRAFT_NO_CATEGORY_LABEL = {
  "경영팀": "경영팀",
  "용역사업": "용역",
  "환경/소독": "환경소독",
  "환경/종합": "환경종합",
};



export default function DraftDocumentNew({ fixedCategory = "경영팀", fixedDocumentType = null }) {
  const { id } = useParams();  // ← 이 한 줄 추가

  // ✅ 불러오기 관련 상태
  const [showLoadModal, setShowLoadModal] = useState(false);

  const [loadCategory, setLoadCategory] = useState("경영팀");
  const [incomeList, setIncomeList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // ✅ 자료 불러오기 모달 — 제목/거래처 검색 필터 + 미작성만 보기 토글 (클라이언트 필터, 서버 재요청 없음)
  const [loadSearchTerm, setLoadSearchTerm] = useState("");
  const [showUndraftedOnly, setShowUndraftedOnly] = useState(true);

  // ✅ 용역 수입기안 청구형(공단 계약) 전용 — 기성계 재조회 모달(회차/계약금액/기기성금액 확정용)
  const [showYongyeokGisungModal, setShowYongyeokGisungModal] = useState(false);
  const [yongyeokGisungList, setYongyeokGisungList] = useState([]);
  const [yongyeokGisungLoading, setYongyeokGisungLoading] = useState(false);
  // ✅ 모달 필터 — 연도/월 셀렉트 + 미작성/작성/전체 토글(기본 미작성). 이 필터들이
  // 바뀔 때마다 아래 useEffect가 재조회한다(모달이 열려있을 때만).
  const [yongyeokGisungYear, setYongyeokGisungYear] = useState(() => new Date().getFullYear());
  const [yongyeokGisungMonth, setYongyeokGisungMonth] = useState(""); // "" = 전체 월
  const [yongyeokGisungDraftedFilter, setYongyeokGisungDraftedFilter] = useState("undrafted"); // "undrafted" | "drafted" | "all"


  
  // ✅ 추가
  const [sourceItemId, setSourceItemId] = useState(null);
  const [sourceEndpoint, setSourceEndpoint] = useState(null);
  const [sourceDraftNo, setSourceDraftNo] = useState("");  // ✅ 여기에 추가
  // ✅ 용역 물품구매 "대상" 자동 채움용 — 불러온 원본의 구분(service_type) 원본값 보관
  const [loadedServiceType, setLoadedServiceType] = useState("");
  // ✅ 용역 물품구매 상품 행 자동 채움용 — 불러온 원본의 품목 내역(items) 배열 보관
  const [loadedServiceItems, setLoadedServiceItems] = useState([]);
  // ✅ 용역 일용직급여 행 자동 채움용 — 불러온 원본의 인건비 내역(salaries) 배열 보관
  const [loadedServiceSalaries, setLoadedServiceSalaries] = useState([]);


  // ✅ 날짜 필터 상태 추가
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState("2026-12-31");

  // ✅ 문서 종류 선택 — fixedDocumentType이 있으면(문서종류별 분리 페이지) 그 값으로 고정
  const [documentType, setDocumentType] = useState(fixedDocumentType || "income_draft");

  // ✅ 자료 불러오기 목록 필터 — documentType 선언 이후로 이동(TDZ 버그 수정: 이전엔 여기서
  // 참조하는 documentType의 useState 선언(바로 위)보다 앞줄에 있어서 "Cannot access
  // 'documentType' before initialization" 런타임 에러가 발생했음. 로직은 변경 없음.
  const filteredIncomeList = incomeList
    // ✅ 용역사업 수입기안은 내부기안/공문이 draft_kind로 짝지어 저장돼 있어, 공문 행은
    // 내부와 데이터가 완전히 동일한 중복이라 자료 불러오기 대상으로 노출할 이유가 없음
    .filter((item) => !(documentType === "income_draft" && loadCategory === "용역사업") || item.draft_kind !== "official")
    .filter((item) => !showUndraftedOnly || !item.is_drafted)
    .filter(
      (item) =>
        !loadSearchTerm ||
        item.title?.includes(loadSearchTerm) ||
        item.partner_name?.includes(loadSearchTerm)
    );

  const [category, setCategory] = useState(fixedCategory);
  const [detailOption, setDetailOption] = useState("");  // ✅ 추가

  const [subcategory, setSubcategory] = useState("");
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date()));
  const [workMonth, setWorkMonth] = useState("");
  const [amount, setAmount] = useState("");
  // ✅ DB에서 불러온 원본 금액 보호용 — 자동합계 템플릿이 amount를 덮어써도 원본을 여기 따로 보관
  const [loadedAmount, setLoadedAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [seq, setSeq] = useState("001");
  const [saving, setSaving] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);  // ✅ 저장 완료 모달
  const [showModal, setShowModal] = useState(false);
  // ✅ 상신 직전 결재라인 재확인 다이얼로그
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [approvers, setApprovers] = useState([]);

  // ✅ 붙임 자료 상태 추가
  const [attachments, setAttachments] = useState([
    { id: 1, name: "", count: 1 }
  ]);

  // 거래처 관련 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");

  // ✅ 물품구매 템플릿 전용 — 품목명(로드됨)
  const [serviceContent, setServiceContent] = useState("");

  // ✅ 법인인감증명서 전용 — 세부내역 자동조립용 원본 items, 템플릿 선택 시점과 무관하게 항상 저장
  const [loadedItems, setLoadedItems] = useState([]);

  // ✅ 기장수수료 전용 — execution_date에서 뽑은 "월" (신용카드의 workMonth와는 별개 state)
  const [executionMonth, setExecutionMonth] = useState("");

  // ✅ 리팩터링 1단계 — 템플릿별 전용 state 통합 (배치 단위로 마이그레이션 중)
  // 배치1: 주차비용(parkingBranch/parkingReason), 법인인감증명서(sealReason),
  //        사업자등록증/영업신고수수료(bizTarget/bizCategory), 임직원급여(payDateRaw),
  //        사무실관리비(maintMonth/maintDueDateRaw)
  const [templateState, setTemplateState] = useState(getInitialTemplateState());
  const setTemplateField = (key, value) => {
    setTemplateState((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ 배치2: 기부금(donationReason/donationTarget), 복지포인트(welfareApplicant/welfareReason),
  //         용역계약(contractName/contractAmountRaw/contractStartRaw/contractEndRaw),
  //         직무교육비(trainee/trainCourse/trainSchedule/trainTarget/trainInstitution) → templateState로 통합

  // ✅ 배치3: 배너광고(adTarget), 전기료(elecMonth/elecUsageStartRaw/elecUsageEndRaw/elecDueDateRaw),
  //         유류비(carNumber/carModel), 소모품구매(supplyPayType) → templateState로 통합

  // ✅ 배치4: 채용심사비(docFee/docCount/docPersons/intFee/intCount/intPersons),
  //         차량할부금(installmentRound), 공제금환급(refundRows/refundNote) → templateState로 통합

  // ✅ 배치5: 퇴직연금(pensionMonth/pensionDueRaw/pensionRows/pensionFeeAmount),
  //         원천세(withholdingRows/withholdingDueRaw), 정수기렌탈료(waterMonth/waterSupply/waterNote),
  //         서버이용료(serverSupply/serverNote) → templateState로 통합 (리팩터링 1단계 마지막 배치)

  // ✅ 템플릿 fields의 input:true 항목들을 범용으로 담는 상태 (예: budgetItem, 일시, 장소, 집행대상 ...)
  const [templateInputs, setTemplateInputs] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // 사용자가 입력칸에 직접 타이핑 — 해당 필드는 이후 자동 채움 대상에서 영구 제외
  const setTemplateInput = (key, value) => {
    setTemplateInputs((prev) => ({ ...prev, [key]: value }));
    setTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  // touched 상태까지 완전히 초기화하고 기본값으로 채움 — 새 자료 불러오기 시 사용
  const resetTemplateInputs = (tplCode) => {
    const tpl = EXPENSE_TEMPLATES[tplCode];
    const next = {};
    (tpl?.fields || []).forEach((f) => {
      if (f.input) next[f.key] = f.default || "";
    });
    setTemplateInputs(next);
    setTouchedFields({});
  };

  // ✅ [추가] 냉난방기 종합세척 상세내역 (미리보기용)
  const [aircondDetails, setAircondDetails] = useState([]);

  // 용역 기성계 작성 시 
  const [tableType, setTableType] = useState(null);  // ✅ 여기

  // ✅ 상세보기 진입 시 기존 데이터 불러오기
  useEffect(() => {
    if (!id) return;
    async function loadDraft() {
      try {
        const { data } = await api.get(`/draft/documents/${id}/`);
        setDocumentType(data.document_type || "income_draft");
        setCategory(data.category || fixedCategory);
        setSubcategory(data.subcategory || "");
        setDetailOption(data.detail_option || "");
        setDraftDate(data.draft_date || toISO(new Date()));
        setClaimDate(data.claim_date || toISO(new Date()));
        setWorkMonth(data.work_month || "");
        setVendorInput(data.partner_name || "");
        // ✅ 일부 원본 API(예: 환경/소독 수입)는 partner를 nested 객체({id, name, ...})로
        // 내려주므로, pk(숫자 id)만 뽑아 저장한다 — payload에 객체째로 보내면 백엔드가
        // "Incorrect type. Expected pk value, received dict." 400을 던진다.
        setVendorId(data.partner?.id ?? data.partner ?? null);
        setAmount(String(data.amount || ""));
        setPayMethodLabel(CODE_TO_PAY_METHOD[data.pay_method] || "계좌이체");
        setTitle(data.title || "");
        setDesc(data.desc || "");
        setSeq(data.seq || "001");
        setApprovers(data.approval_lines || []);
        // ✅ 리팩터링 ② — 템플릿 입력값 복원 (구버전 기안서는 template_data가 없으므로 기본값으로 대체)
        setTemplateState({ ...getInitialTemplateState(), ...(data.template_data || {}) });
        setIsDataLoaded(true);
      } catch (err) {
        console.error("❌ 기안서 불러오기 실패:", err);
        alert("기안서를 불러오는 데 실패했습니다.");
      }
    }
    loadDraft();
  }, [id]);

  // ✅ 모달이 열릴 때 목록 로드
  useEffect(() => {
    if (showLoadModal) {
      fetchIncomeList();
    }
  }, [showLoadModal, loadCategory, startDate, endDate, documentType]); // ✅ documentType 추가!

  // ✅ 수입/지출 목록 불러오기
  const fetchIncomeList = async () => {
    setLoadingList(true);
    try {
      // ✅ 문서 종류와 카테고리 모두 고려
      const endpoint = CATEGORY_ENDPOINTS[documentType]?.[loadCategory];
      
      if (!endpoint) {
        console.error("❌ 잘못된 문서 종류 또는 카테고리:", documentType, loadCategory);
        setIncomeList([]);
        return;
      }
  
      
      const { data } = await api.get(endpoint);
      console.log("🔎 호출 endpoint:", endpoint, "| loadCategory:", loadCategory);
      const list = Array.isArray(data) ? data : data.results || [];
      console.log("🔎 응답 건수:", list.length, "| 첫 행:", list[0]);
      // ⚠️ 용역사업(income-service)의 draft_kind=official 행은 여기서 걸러내지 않는다 —
      // 내부기안 선택 시 짝인 공문 draft_no를 이 목록에서 그대로 찾아 써야 하기 때문
      // (표시 단계에서만 제외 — filteredIncomeList 참고).


      // ✅ 날짜 범위 필터링 추가 (기안 작성 여부와 무관하게 전체 표시 — "미작성만 보기" 토글로 클라이언트에서 걸러냄)
      let undraftedList = list;
      if (startDate && endDate) {
        undraftedList = undraftedList.filter(item => {
          const itemDate = item.draft_date || item.claim_date || "";
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      // ✅ partner_name이 비어있으면 partners 배열에서 생성
      undraftedList = undraftedList.map(item => {
        if (!item.partner_name || item.partner_name.trim() === "") {
          if (item.partners && Array.isArray(item.partners) && item.partners.length > 0) {
            // 거래처가 1개면 그 이름만
            if (item.partners.length === 1) {
              item.partner_name = item.partners[0].name || "";
            } 
            // 거래처가 여러 개면 "거래처1 외 N곳"
            else {
              const firstName = item.partners[0].name || "거래처";
              const othersCount = item.partners.length - 1;
              item.partner_name = `${firstName} 외 ${othersCount}곳`;
            }
          } else {
            item.partner_name = "거래처 미지정";
          }
        }
        return item;
      });
      
      setIncomeList(undraftedList);
    } catch (err) {
      console.error("❌ 목록 불러오기 실패:", err);
      setIncomeList([]);
    } finally {
      setLoadingList(false);
    }
  };

  // 194번째 줄 - handleSelectData
  const handleSelectData = (item) => {
    console.log("📥 불러온 데이터:", item);
    
    if (item.document_type) {
      setDocumentType(item.document_type);
    }
    
    setCategory(loadCategory);
    
    setVendorInput(item.partner_name || "");
    // ✅ 소독 수입데이터 등 일부 원본 API는 partner를 nested 객체({id, name, ...})로
    // 내려주므로, pk(숫자 id)만 뽑아 저장한다(loadDraft와 동일한 처리).
    setVendorId(item.partner?.id ?? item.partner ?? null);
    setShowVendorList(false);  // ✅ 추가!
    setHighlightIndex(-1);     // ✅ 추가!
    
    const loadedSubcategory = item.subcategory || item.detail_type || "";

    if (loadedSubcategory) {
      setSubcategory(loadedSubcategory);
      console.log("✅ 세부구분 설정:", loadedSubcategory);
    } else {
      setSubcategory("");
    }


    setWorkMonth(item.work_month || "");
    setAmount(String(resolveItemAmount(item) || ""));
    setLoadedAmount(String(resolveItemAmount(item) || ""));
    setDesc(item.desc || "");
    setTitle(item.title || "");
    
    // ✅ 작성일 설정
    setDraftDate(item.draft_date || toISO(new Date()));
    setClaimDate(item.claim_date || toISO(new Date()));
    
    if (item.pay_method) {
      setPayMethodLabel(CODE_TO_PAY_METHOD[item.pay_method] || "계좌이체");
    }

    setServiceContent(item.items?.[0]?.name || item.service_content || "");
    setLoadedItems(item.items || []);
    setExecutionMonth(formatYearMonth(item.execution_date));
    // 계좌이체 계열이면 transfer, 그 외(카드/현금 등)는 기본 card로 자동 판별
    const supplyPayLabel = CODE_TO_PAY_METHOD[item.pay_method] || item.pay_method || "";
    setTemplateState((prev) => ({
      ...prev,
      ...getInitialTemplateState(),
      // ✅ 사무실관리비/전기료/퇴직연금은 연도 없는 "M월" 표기가 원본 형식이라
      // extractYearMonth("YYYY년 M월")로 뽑은 뒤 normalizeMonthOnly로 월만 남긴다.
      maintMonth: normalizeMonthOnly(extractYearMonth(item.items?.[0]?.name || item.service_content || "")),
      elecMonth: normalizeMonthOnly(extractYearMonth(item.items?.[0]?.name || item.service_content || "")),
      supplyPayType: supplyPayLabel.includes("이체") ? "transfer" : "card",
      pensionMonth: normalizeMonthOnly(extractYearMonth(item.items?.[0]?.name || item.service_content || "")),
      utilityMonth: extractYearMonth(item.items?.[0]?.name || item.service_content || ""),
    }));
    // 새 자료를 불러올 때마다 이전 입력값(touched 포함) 초기화 — 현재 선택된 템플릿의 기본값으로 채움
    resetTemplateInputs(detailOption);

    // ✅ 법인인감증명서 전용 — 자료 로드 시점에 이미 corporate_seal이 선택돼 있으면 즉시 조립
    if (detailOption === "corporate_seal") {
      const detailText = assembleSealDetail(item.items);
      if (detailText) {
        setTemplateInputs((prev) => ({ ...prev, detail: detailText }));
      }
    }

    if (item.seq) {
      setSeq(String(item.seq).padStart(3, "0"));
    }

    if (item.draft_no) {
      setSourceDraftNo(item.draft_no);
    }

    // ✅ 용역사업 청구형 — 내부기안(internal) 선택 시, 이미 불러온 목록(incomeList)에서
    // 같은 날짜·다음 순번(seq+1)·draft_kind=official인 짝을 찾아 공문 문서번호로 저장
    if (item.draft_kind === "internal" && loadCategory === "용역사업") {
      const officialMatch = incomeList.find(
        (row) =>
          row.draft_kind === "official" &&
          row.draft_date === item.draft_date &&
          row.seq === item.seq + 1
      );
      setTemplateField("officialDraftNo", officialMatch?.draft_no || "");
    }


    
    const displayAmount = resolveItemAmount(item).toLocaleString();
    
    // ✅ 문서 종류 레이블
    const docTypeLabel = {
      income_draft: "수입기안",
      expense_draft: "지출기안",
      income_resolution: "수입결의",
      expense_resolution: "지출결의"
    }[item.document_type || documentType] || "기안";
    
    alert(`✅ 데이터를 불러왔습니다.\n문서종류: ${docTypeLabel}\n카테고리: ${loadCategory}\n세부구분: ${loadedSubcategory}\n거래처: ${item.partner_name}\n금액: ${displayAmount}원`);
    setShowLoadModal(false);
    setIsDataLoaded(true);  // ✅ 추가

    
    // ✅ [추가] 냉난방기 상세내역: quantity > 0 AND unit_price > 0 인 행만 추출
    const validDetails = (item.aircond_details || []).filter(
      d => (d.quantity || 0) > 0 && (d.unit_price || 0) > 0
    );
    setAircondDetails(validDetails);

    setSourceItemId(item.id);
    setSourceEndpoint(CATEGORY_ENDPOINTS[documentType]?.[loadCategory]);
    // ✅ 용역 물품구매 "대상" 자동 채움용 — 구분(service_type) 원본값 보관
    setLoadedServiceType(item.service_type || "");
    // ✅ 용역 물품구매 상품 행 자동 채움용 — 품목 내역(items) 원본값 보관
    setLoadedServiceItems(item.items || []);
    // ✅ 용역 일용직급여 행 자동 채움용 — 인건비 내역(salaries) 원본값 보관
    setLoadedServiceSalaries(item.salaries || []);

  };

  // ✅ 용역 수입기안 청구형(공단 계약) 전용 — 기성계 재조회 목록. 연도/월/미작성-작성
  // 필터를 서버 쿼리 파라미터로 그대로 넘긴다(gisung_gye/views.py GisungRecordViewSet).
  const fetchYongyeokGisungList = async () => {
    const config = VENDOR_CONFIG[vendorInput];
    if (!config?.contractId) return;
    setYongyeokGisungLoading(true);
    try {
      const params = { contract: config.contractId };
      if (yongyeokGisungYear) params.year = yongyeokGisungYear;
      if (yongyeokGisungMonth) params.month = yongyeokGisungMonth;
      if (yongyeokGisungDraftedFilter !== "all") {
        params.is_drafted = yongyeokGisungDraftedFilter === "drafted";
      }
      const { data } = await api.get("gisung-gye/records/", { params });
      const list = Array.isArray(data) ? data : data.results || [];
      setYongyeokGisungList([...list].sort((a, b) => b.gisung_round - a.gisung_round));
    } catch (err) {
      console.error("❌ 기성계 목록 불러오기 실패:", err);
      setYongyeokGisungList([]);
    } finally {
      setYongyeokGisungLoading(false);
    }
  };

  // ✅ 모달이 열려있는 동안 연도/월/미작성-작성 필터가 바뀔 때마다 재조회. 모달을 여는
  // 버튼(TemplateFormFields.jsx)은 setShowYongyeokGisungModal(true)만 호출하면 되고,
  // 최초 조회도 이 effect가 담당한다(중복 호출 방지 — 예전엔 버튼 onClick이 직접 불렀음).
  useEffect(() => {
    if (!showYongyeokGisungModal) return;
    fetchYongyeokGisungList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showYongyeokGisungModal, yongyeokGisungYear, yongyeokGisungMonth, yongyeokGisungDraftedFilter]);

  // ✅ 회차/계약금액/기기성금액/전회누계 필드 확정 + 제목/본문 세팅(계산된 값을 그대로
  // 받는다 — 회차를 어떻게 정했는지는 호출부 책임)
  // recordId — 이 회차 계산의 "기준 기성계 레코드"(자동매칭이면 매칭된 레코드 자신,
  // 수동 선택이면 선택한 레코드) id. 저장 시 DraftDocument.gisung_record로 그대로
  // 저장되어 "기성계 불러오기" 모달의 미작성/작성 필터 판정 근거가 된다.
  const commitYongyeokFields = ({ round, contractAmount, prevAmount, prevCumulative, config, warning = false, recordId = null }) => {
    setTemplateField("yongyeokRound", String(round));
    setTemplateField("yongyeokContractAmount", String(contractAmount));
    setTemplateField("yongyeokPrevAmount", String(prevAmount));
    setTemplateField("yongyeokPrevCumulative", String(prevCumulative));
    setTemplateField("yongyeokContractName", config?.contractName || "");
    setTemplateField("yongyeokReferenceLabel", config?.referenceLabel || "");
    setTemplateField("yongyeokRoundWarning", warning);
    setTemplateField("yongyeokGisungRecordId", recordId);

    // ✅ 제목/본문은 회차가 확정된 이 시점에만 세팅(ServiceGiseongAutoFill에서는 건너뜀)
    const siteTitle = (config?.contractName || "").replace(/^\d{4}년\s*/, "");
    const [workYear] = (workMonth || "").split("-");
    const year = workYear || String(new Date().getFullYear());
    setTitle(`${year}년 ${siteTitle} 제${round}회 기성금 청구`);
    setDesc(`${year}년 ${siteTitle} 제${round}회 기성금을 아래와 같이 청구하고자 합니다.`);
  };

  // ✅ 전회차 레코드 하나로부터 "다음 회차(N+1)" 필드값 계산 — 기성계 이력 모달에서 특정
  // 과거 회차를 전회차 기준으로 수동 지정할 때, 그리고 회차 자동 매칭 실패 시 폴백(최신
  // 회차+1)에 공통으로 사용. prev_amount 필드는 신뢰하지 않고(실측 결과 round 3~4 이후
  // 어긋나는 데이터 오류 다수 확인) 레코드 자신의 current_amount/remaining_amount를 쓴다.
  const fieldsFromPrevRecord = (prevRecord) => {
    const round = (prevRecord?.gisung_round ?? 0) + 1;
    const contractAmount = Number(prevRecord?.contract_amount) || 0;
    const prevAmount = prevRecord ? Number(prevRecord.current_amount) || 0 : 0;
    const prevCumulative = contractAmount - (prevRecord ? Number(prevRecord.remaining_amount) || 0 : contractAmount);
    return { round, contractAmount, prevAmount, prevCumulative };
  };

  // ✅ 자료 불러오기로 불러온 수입 데이터 자신이 "전회차"가 아니라 "이번 청구 회차 자신"
  // 이다 — 같은 계약의 기성계 레코드 중 금액+귀속월이 일치하는 레코드를 찾아 그 회차(N)를
  // 그대로 쓰고, 기기성금액/전회누계는 N-1회차 레코드에서 가져온다(과거엔 이걸 착각해
  // 최신 회차+1로 잘못 채번해 청구금액=기기성금액이 되는 버그가 있었음). 일치하는 레코드가
  // 없으면(아직 기성계 생성 페이지에 등록되지 않은 다음 회차) 최신 회차+1로 폴백하되
  // 화면에 "회차 자동 판별 실패" 경고를 띄운다.
  useEffect(() => {
    // ✅ vendor 매칭(기성계 재조회)은 용역사업 카테고리일 때만 발동 — 다른 카테고리의
    // vendor 문자열이 우연히 VENDOR_CONFIG 키와 겹치는 사고를 막기 위한 명시적 가드
    if (category !== "용역사업" || tableType !== "yongyeok_claim") return;
    const config = VENDOR_CONFIG[vendorInput];
    if (!config?.contractId) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get("gisung-gye/records/", { params: { contract: config.contractId } });
        const records = Array.isArray(data) ? data : data.results || [];
        if (cancelled) return;

        const claimAmount = Number(amount) || 0;
        const [wYear, wMonth] = (workMonth || "").split("-");
        const monthNum = wMonth ? parseInt(wMonth, 10) : null;

        const matched = records.find(
          (r) =>
            Number(r.current_amount) === claimAmount &&
            String(r.gisung_year) === String(wYear) &&
            Number(r.gisung_month) === monthNum
        );

        if (matched) {
          const prevRecord = records.find((r) => r.gisung_round === matched.gisung_round - 1) || null;
          const contractAmount = Number(matched.contract_amount) || 0;
          const prevAmount = prevRecord ? Number(prevRecord.current_amount) || 0 : 0;
          const prevCumulative = contractAmount - (prevRecord ? Number(prevRecord.remaining_amount) || 0 : contractAmount);
          commitYongyeokFields({
            round: matched.gisung_round,
            contractAmount,
            prevAmount,
            prevCumulative,
            config,
            warning: false,
            recordId: matched.id,
          });
          return;
        }

        // ✅ 매칭 실패 — 최신 회차+1 폴백(화면 경고)
        const last = records.reduce((max, r) => (r.gisung_round > (max?.gisung_round ?? 0) ? r : max), null);
        let fields;
        if (last) {
          fields = fieldsFromPrevRecord(last);
        } else {
          // ✅ 이력이 전혀 없는 계약(제1회차) — 계약 단건 조회로 contract_amount 확보
          const { data: contract } = await api.get(`gisung-gye/contracts/${config.contractId}/`);
          fields = fieldsFromPrevRecord({
            gisung_round: 0,
            current_amount: 0,
            remaining_amount: contract.contract_amount,
            contract_amount: contract.contract_amount,
          });
        }
        if (cancelled) return;
        // ✅ last를 "기준 삼아" N+1을 계산했으므로 수동 선택(handlePickYongyeokGisung)과
        // 동일하게 last.id를 기준 레코드로 기록(없으면(제1회차) null 그대로).
        commitYongyeokFields({ ...fields, config, warning: true, recordId: last?.id ?? null });
      } catch (err) {
        console.error("❌ 기성계 자동 조회 실패:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [category, tableType, vendorInput, amount, workMonth]);

  // ✅ 기성계 이력 모달에서 특정 과거 회차를 수동으로 다시 기준 삼고 싶을 때(예: 정정/재기안)
  const handlePickYongyeokGisung = (record) => {
    const config = VENDOR_CONFIG[vendorInput];
    commitYongyeokFields({ ...fieldsFromPrevRecord(record), config, warning: false, recordId: record.id });
    setShowYongyeokGisungModal(false);
  };

  // ✅ 환경/종합 개인고객 대금청구 전용 — 성명(=거래처명 자유입력)에서 제목/본문 자동
  // 유도. 표 데이터(냉난방기 종합세척 상세)는 자료 불러오기 시 이미 aircondDetails로
  // 채워지므로(handleSelectData) 여기서는 제목/본문만 담당 — 용역 청구형 인프라
  // (tableType/기성계 재조회) 사용 안 함.
  useEffect(() => {
    if (category !== "환경/종합" || detailOption !== "개인고객 대금청구") return;
    if (!vendorInput) return;
    setTitle(`개인 고객 ${vendorInput}님 냉난방기 종합세척 대금청구 건`);
    setDesc(`개인 고객 ${vendorInput}님 냉난방기 종합세척 대금을 아래와 같이 청구하고자 합니다.`);
  }, [category, detailOption, vendorInput]);

  // ✅ 기안번호 생성
  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);

  const draftNo = useMemo(() => {
    // ✅ 원본 번호가 있으면 그걸 우선 사용
    if (sourceDraftNo) return sourceDraftNo;

    // ✅ 수입기안 전용 — "수입기안" 문자열 제거, 카테고리를 채번용 표시명으로 교체
    // (환경/소독처럼 "/"가 들어간 카테고리명이 그대로 채번에 섞이는 문제도 함께 해결).
    // 지출기안 등 다른 문서종류는 기존 포맷 그대로 유지.
    if (documentType === "income_draft") {
      const label = INCOME_DRAFT_NO_CATEGORY_LABEL[category] || category.replace(/\//g, "");
      return `금천일자리(주)${label}-${yymmdd}-${seq}호`;
    }

    const typePrefix = {
      expense_draft: "지출기안",
      income_resolution: "수입결의",
      expense_resolution: "지출결의",
    }[documentType] || "기안";

    return `금천일자리(주)${category}-${typePrefix}-${yymmdd}-${seq}호`;
  }, [sourceDraftNo, documentType, category, yymmdd, seq]);

  // ✅ 작성일 변경 시 청구일 자동 설정
  useEffect(() => {
    if (id) return;  // ✅ 상세보기 진입 시 청구일 덮어쓰기 방지

    setClaimDate(draftDate);
  }, [draftDate]);

  // ✅ seq 자동 생성
  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    async function getSeq() {
      if (id) return;           // 수정 모드 방지
      if (isDataLoaded) return; // ✅ 자료 불러온 후 seq 덮어쓰기 방지

      if (!category || !draftDate) return;
      try {
        const { data } = await api.get(`/draft/documents/next-seq/?date=${draftDate}&category=${category}&document_type=${documentType}`);


        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (e) {
        console.error("[next-seq] failed:", e?.response?.status);
      }
    }
    getSeq();
    return () => { ignore = true; };
  }, [category, draftDate, documentType, isDataLoaded, seqRefreshTick]); // ✅ isDataLoaded 추가



  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      try {
        const res = await api.get("vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      }
    }
    fetchVendors();
  }, []);

  // ✅ 거래처 자동완성 필터
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 거래처 입력
  const handleChangeVendor = (e) => {
    // ✅ 자료 불러오기로 채워진 거래처는 잠금 — DB 원본과 어긋나지 않도록 onChange도 차단
    if (isDataLoaded) return;
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 키보드 네비게이션
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < filteredVendors.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filteredVendors.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 제목 자동 생성
  useEffect(() => {
    if (id) return;  // ✅ 상세보기 진입 시 제목 자동생성 방지

    // ✅ 용역 기성계는 ServiceGiseongAutoFill이 처리하므로 여기서 제외
    const isGiseong =
      documentType === "income_draft" &&
      category === "용역사업" &&
      detailOption === "기성계";
    if (isGiseong) return;  // ← 이 줄 추가

    // ✅ 지출 경영팀/용역사업 — 신용카드 / 물품구매 등 제목 자동생성
    if (documentType === "expense_draft" && (category === "경영팀" || category === "용역사업" || category === "환경/종합" || category === "CAFE_INN_가산" || category === "CAFE_INN_독산" || category === "CAFE_INN_금나래" || category === "CAFE_INN_그린")) {
      if (detailOption === "service_purchase") {
        setTitle(
          templateState.purchaseTarget && serviceContent
            ? `${templateState.purchaseTarget} ${serviceContent} 구매`
            : ""
        );
      } else if (detailOption === "service_commission_health") {
        setTitle(
          templateState.commissionMonth ? `${templateState.commissionMonth} 보건관리 위탁 수수료 결제` : ""
        );
      } else if (detailOption === "service_commission_safety") {
        setTitle(
          templateState.commissionMonth ? `${templateState.commissionMonth} 산업안전관리업무 위탁 수수료 결제` : ""
        );
      } else if (detailOption === "service_daily_wage") {
        setTitle(
          templateState.dailyWageMonth ? `${templateState.dailyWageMonth} 일용직 현장근로자 급여 지급` : ""
        );
      } else if (detailOption === "env_total_car_lease") {
        setTitle("금천일자리주식회사 업무용 포터 차량 리스료 지출");
      } else if (detailOption === "gasan_ingredient") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 재료 구매` : "");
      } else if (detailOption === "doksan_ingredient") {
        setTitle(templateState.gasanMonth ? `카페인 독산점 ${templateState.gasanMonth} 재료 구매` : "");
      } else if (detailOption === "geumnarae_ingredient") {
        setTitle(templateState.gasanMonth ? `카페인 금나래점 ${templateState.gasanMonth} 재료 구매` : "");
      } else if (detailOption === "green_ingredient") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 재료 구매` : "");
      } else if (detailOption === "gasan_goods") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 상품 구매` : "");
      } else if (detailOption === "doksan_goods") {
        setTitle(templateState.gasanMonth ? `카페인 독산점 ${templateState.gasanMonth} 상품 구매` : "");
      } else if (detailOption === "geumnarae_goods") {
        setTitle(templateState.gasanMonth ? `카페인 금나래점 ${templateState.gasanMonth} 상품 구매` : "");
      } else if (detailOption === "green_goods") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 상품 구매` : "");
      } else if (detailOption === "gasan_supplies") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 소모품 구매` : "");
      } else if (detailOption === "geumnarae_supplies") {
        setTitle(templateState.gasanMonth ? `카페인 금나래점 ${templateState.gasanMonth} 소모품 구매` : "");
      } else if (detailOption === "green_supplies") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 소모품 구매` : "");
      } else if (detailOption === "gasan_pos_rental") {
        setTitle("카페인 가산점 포스 임대료 납부");
      } else if (detailOption === "doksan_pos_rental") {
        setTitle("카페인 독산점 포스 임대료 납부");
      } else if (detailOption === "geumnarae_pos_rental") {
        setTitle("카페인 금나래점 포스 임대료 납부");
      } else if (detailOption === "green_pos_rental") {
        setTitle("카페인 그린점 포스 임대료 납부");
      } else if (detailOption === "gasan_internet") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 인터넷 및 전화 요금 납부` : "");
      } else if (detailOption === "doksan_internet") {
        setTitle(templateState.gasanMonth ? `카페인 독산점 ${templateState.gasanMonth} 인터넷 및 전화 통신 요금 납부` : "");
      } else if (detailOption === "geumnarae_internet") {
        setTitle(templateState.gasanMonth ? `카페인 금나래점 ${templateState.gasanMonth} 인터넷 및 전화 통신 요금 납부` : "");
      } else if (detailOption === "green_internet") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 인터넷 및 전화 통신 요금 납부` : "");
      } else if (detailOption === "doksan_holiday_gift") {
        setTitle("카페인 독산점 바리스타 복리후생비(명절격려품) 지출");
      } else if (detailOption === "geumnarae_holiday_gift") {
        setTitle("카페인 금나래점 바리스타 복리후생비(명절격려품) 지출");
      } else if (detailOption === "green_holiday_gift") {
        setTitle("카페인 그린점 바리스타 복리후생비(명절격려품) 지출");
      } else if (detailOption === "doksan_withholding_tax") {
        setTitle("카페인(CAFE_INN) 독산점 근로소득 원천세 납부");
      } else if (detailOption === "geumnarae_withholding_tax") {
        setTitle("카페인(CAFE_INN) 금나래점 근로소득 원천세 납부");
      } else if (detailOption === "green_withholding_tax") {
        setTitle("카페인(CAFE_INN) 그린점 근로소득 원천세 납부");
      } else if (detailOption === "doksan_vending_terminal") {
        setTitle("카페인 독산점 자판기용 단말기 이용료 납부");
      } else if (detailOption === "green_vending_terminal") {
        setTitle("카페인 그린점 자판기용 단말기 이용료 납부");
      } else if (detailOption === "doksan_vat") {
        setTitle(
          templateState.vatYear && templateState.vatQuarter
            ? `CAFE_INN 독산점 ${templateState.vatYear}년 ${templateState.vatQuarter}기 예정고지 부가세 납부`
            : ""
        );
      } else if (detailOption === "geumnarae_vat") {
        setTitle(
          templateState.vatYear && templateState.vatQuarter && templateState.vatType
            ? `CAFE_INN 금나래점 ${templateState.vatYear}년 ${templateState.vatQuarter}기 ${templateState.vatType} 부가세 납부`
            : ""
        );
      } else if (detailOption === "green_vat") {
        setTitle(
          templateState.vatYear && templateState.vatQuarter && templateState.vatType
            ? `CAFE_INN 그린점 ${templateState.vatYear}년 ${templateState.vatQuarter}기 ${templateState.vatType} 부가세 납부`
            : ""
        );
      } else if (detailOption === "doksan_hygiene_edu") {
        setTitle(templateState.hygieneYear ? `카페인 독산점 ${templateState.hygieneYear}년 위생교육 이수 결제` : "");
      } else if (detailOption === "green_hygiene_edu") {
        setTitle(templateState.hygieneYear ? `카페인 그린점 ${templateState.hygieneYear}년 위생교육 이수 결제` : "");
      } else if (
        [
          "doksan_water", "doksan_gas", "doksan_electric",
          "geumnarae_water", "geumnarae_gas", "geumnarae_electric",
          "green_water", "green_gas", "green_electric",
        ].includes(detailOption)
      ) {
        // ✅ 공과금 3종(독산/금나래/그린 공용) — 수도만 "{월}월 수도요금", 나머지는 "{귀속연월}분 {요금명}" 포맷(사용자 지정)
        const CAFE_UTILITY_INFO = {
          doksan_water: { storeLabel: "독산점", feeLabel: "수도요금" },
          doksan_gas: { storeLabel: "독산점", feeLabel: "가스요금" },
          doksan_electric: { storeLabel: "독산점", feeLabel: "전기료" },
          geumnarae_water: { storeLabel: "금나래점", feeLabel: "수도요금" },
          geumnarae_gas: { storeLabel: "금나래점", feeLabel: "가스요금" },
          geumnarae_electric: { storeLabel: "금나래점", feeLabel: "전기료" },
          green_water: { storeLabel: "그린점", feeLabel: "수도요금" },
          green_gas: { storeLabel: "그린점", feeLabel: "가스요금" },
          green_electric: { storeLabel: "그린점", feeLabel: "전기료" },
        };
        const { storeLabel, feeLabel } = CAFE_UTILITY_INFO[detailOption];
        if (!templateState.gasanMonth) {
          setTitle("");
        } else if (detailOption.endsWith("_water")) {
          const monthNum = (templateState.gasanMonth.match(/(\d+)월/) || [])[1] || "";
          setTitle(`카페인 ${storeLabel} ${monthNum}월 수도요금 납부`);
        } else {
          setTitle(`카페인 ${storeLabel} ${templateState.gasanMonth}분 ${feeLabel} 납부`);
        }
      } else if (detailOption === "gasan_credit_card") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 신용카드 결제대금 납부` : "");
      } else if (detailOption === "doksan_credit_card") {
        setTitle(templateState.gasanMonth ? `카페인 독산점 ${templateState.gasanMonth} 신용카드 결제대금 납부` : "");
      } else if (detailOption === "geumnarae_credit_card") {
        setTitle(templateState.gasanMonth ? `카페인 금나래점 ${templateState.gasanMonth} 신용카드 결제대금 납부` : "");
      } else if (detailOption === "green_credit_card") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 신용카드 결제대금 납부` : "");
      } else if (detailOption === "gasan_payroll") {
        setTitle(templateState.gasanMonth ? `${templateState.gasanMonth} 카페인(CAFE_INN) 가산점 급여 지급의 건` : "");
      } else if (detailOption === "doksan_payroll") {
        setTitle(templateState.gasanMonth ? `${templateState.gasanMonth} 카페인(CAFE_INN) 독산점 급여 지급의 건` : "");
      } else if (detailOption === "geumnarae_payroll") {
        setTitle(templateState.gasanMonth ? `${templateState.gasanMonth} 카페인(CAFE_INN) 금나래점 급여 지급의 건` : "");
      } else if (detailOption === "green_payroll") {
        setTitle(templateState.gasanMonth ? `${templateState.gasanMonth} 카페인(CAFE_INN) 그린점 급여 지급의 건` : "");
      } else if (detailOption === "gasan_social_insurance") {
        setTitle(
          templateState.gasanMonth
            ? `카페인(CAFE_INN) 가산점 ${templateState.gasanMonth}분 사회보험료 납부 건`
            : ""
        );
      } else if (detailOption === "doksan_social_insurance") {
        setTitle(
          templateState.gasanMonth
            ? `카페인(CAFE_INN) 독산점 ${templateState.gasanMonth}분 사회보험료 납부 건`
            : ""
        );
      } else if (detailOption === "geumnarae_social_insurance") {
        setTitle(
          templateState.gasanMonth
            ? `카페인(CAFE_INN) 금나래점 ${templateState.gasanMonth}분 사회보험료 납부 건`
            : ""
        );
      } else if (detailOption === "green_social_insurance") {
        setTitle(
          templateState.gasanMonth
            ? `카페인(CAFE_INN) 그린점 ${templateState.gasanMonth}분 사회보험료 납부 건`
            : ""
        );
      } else if (detailOption === "gasan_maintenance") {
        setTitle(templateState.gasanMonth ? `카페인 가산점 ${templateState.gasanMonth} 관리비 납부` : "");
      } else if (detailOption === "green_maintenance") {
        setTitle(templateState.gasanMonth ? `카페인 그린점 ${templateState.gasanMonth} 관리비 납부` : "");
      } else if (detailOption === "general_purchase") {
        setTitle(serviceContent ? `금천일자리주식회사 ${serviceContent} 구매` : "");
      } else if (detailOption === "credit_card") {
        setTitle(`금천일자리주식회사 ${templateState.creditCardMonth || "2026년 5월"} 신용카드 결제대금 납부`);
      } else if (detailOption === "meeting_expense") {
        setTitle("금천일자리주식회사 업무추진 간담회비 지출");
      } else if (detailOption === "accounting_fee") {
        setTitle(`금천일자리주식회사 ${templateState.accountingMonth || "2026년 5월"} 기장수수료 지출`);
      } else if (detailOption === "utility_fee") {
        setTitle(templateState.utilityMonth ? `금천일자리주식회사 사무실 ${templateState.utilityMonth} 인터넷 및 전화요금 납부` : "");
      } else if (detailOption === "parking_fee") {
        setTitle("금천일자리주식회사 주차비용 지출 건");
      } else if (detailOption === "corporate_seal") {
        setTitle("금천일자리주식회사 법인인감증명서 발급 비용 지출 건");
      } else if (detailOption === "fund_transfer") {
        setTitle(vendorInput ? `${vendorInput} 통장 보전일시자금 이체` : "");
      } else if (detailOption === "biz_license") {
        setTitle(templateState.bizTarget ? `${templateState.bizTarget} 사업자등록증 발급 비용 지출` : "");
      } else if (detailOption === "biz_report") {
        setTitle(templateState.bizTarget ? `${templateState.bizTarget} 영업신고 수수료 지출` : "");
      } else if (detailOption === "supplies" || detailOption === "supplies_office") {
        setTitle(serviceContent ? `금천일자리주식회사 소모품(${serviceContent}) 구매` : "");
      } else if (detailOption === "payroll") {
        // ✅ 기존엔 serviceContent(내용명)에서 월을 추출했으나, 이 필드는 지급일과 무관한
        // 자유 텍스트라 사실상 항상 빈 값 → 제목이 안 만들어짐. 지급일(payDateRaw) 기준으로 변경.
        const month = formatYearMonth(templateState.payDateRaw);
        setTitle(month ? `${month} 임직원 급여 지급` : "");
      } else if (detailOption === "printer_rental") {
        setTitle("금천일자리주식회사 사무실 복합기 렌탈료 지출");
      } else if (detailOption === "office_maintenance") {
        setTitle(templateState.maintMonth ? `금천일자리주식회사 사무실 ${templateState.maintMonth}분 관리비 납부` : "");
      } else if (detailOption === "donation") {
        setTitle("금천일자리주식회사 기부금 지출");
      } else if (detailOption === "postage") {
        setTitle("금천일자리주식회사 우편 발송");
      } else if (detailOption === "welfare_points") {
        setTitle("직원 맞춤형 복지비용(복지포인트) 지급");
      } else if (detailOption === "service_contract") {
        setTitle(
          category === "환경/종합" && templateState.contractName
            ? `${templateState.contractName} 용역 계약 인지세 납부`
            : templateState.contractName
        );
      } else if (detailOption === "job_training") {
        setTitle(templateState.trainee ? `금천일자리주식회사 ${templateState.trainee} 직무 교육비 지출` : "");
      } else if (detailOption === "banner_ad") {
        setTitle(templateState.adTarget ? `금천일자리주식회사 ${templateState.adTarget} 온라인 배너 광고` : "");
      } else if (detailOption === "electric_bill") {
        setTitle(templateState.elecMonth ? `금천일자리주식회사 사무실 ${templateState.elecMonth}분 전기료 납부` : "");
      } else if (detailOption === "fuel_cost") {
        setTitle(templateState.carModel ? `금천일자리주식회사 업무용 차량(${templateState.carModel}) 유류비 지출` : "");
      } else if (detailOption === "meal_allowance") {
        setTitle("금천일자리주식회사 직원 급량비 지출");
      } else if (detailOption === "interview_fee") {
        setTitle("현장근로자 채용 서류 및 면접심사비 지급");
      } else if (detailOption === "car_installment") {
        setTitle(templateState.installmentRound ? `금천일자리주식회사 업무용 특장 차량 ${templateState.installmentRound}회차 할부금 지출` : "");
      } else if (detailOption === "deduction_refund") {
        setTitle("현장근로자 일용직 공제금 환급 건");
      } else if (detailOption === "pension") {
        setTitle(templateState.pensionMonth ? `${templateState.pensionMonth}분 퇴직연금 납부` : "");
      } else if (detailOption === "withholding_tax") {
        setTitle("근로소득 원천세 납부");
      } else if (detailOption === "water_rental") {
        setTitle(templateState.waterMonth ? `금천일자리주식회사 ${templateState.waterMonth} 사무실 정수기 렌탈료 납부` : "");
      } else if (detailOption === "server_fee") {
        setTitle("금천일자리주식회사 홈페이지 서버이용료 납부");
      } else {
        setTitle("");
      }
      return;  // ← 지출 경영팀은 여기서 끝, 아래 수입 전용 로직 안 탐
    }

    if (vendorInput && subcategory && workMonth) {
      let year = "", month = "";

      if (workMonth.includes("-")) {
        [year, month] = workMonth.split("-");
      } else if (workMonth.includes("년")) {
        year = workMonth.split("년")[0].trim();
        month = workMonth.split("년")[1].replace("월", "").trim();
      }

      if (month && month.length === 1) month = "0" + month;

      const cleanVendor = vendorInput.replace(/\(.*?\)/g, "").trim();

      if (year && month) {
        // ✅ 수입기안-소독-대금청구일 때 전용 제목

        const isClaimTable = 
          documentType === "income_draft" && 
          (category === "환경/소독" || category === "환경/종합") && 
          detailOption === "대금청구";

        // ✅ 냉난방기 종합세척 전용 제목
        const isAircond =
          isClaimTable &&
          category === "환경/종합" &&
          subcategory === "냉난방기 종합세척";

        if (isAircond) {
          setTitle(`${cleanVendor} 냉난방기 종합세척 대금청구 건`);
        } else if (isClaimTable) {
          setTitle(`${cleanVendor} ${subcategory} ${parseInt(month)}월분 대금청구 건`);
        } else {
          setTitle(`${year}년 ${month}월 ${cleanVendor} ${subcategory}`);
        }


      }
    } else {
      setTitle("");
    }
  }, [vendorInput, subcategory, workMonth, documentType, category, detailOption, serviceContent, executionMonth, templateState.bizTarget, templateState.maintMonth, templateState.contractName, templateState.trainee, templateState.adTarget, templateState.elecMonth, templateState.carModel, templateState.installmentRound, templateState.pensionMonth, templateState.waterMonth, templateState.purchaseTarget, templateState.commissionMonth, templateState.dailyWageMonth, templateState.gasanMonth, templateState.payDateRaw, templateState.utilityMonth, templateState.creditCardMonth, templateState.accountingMonth]);

  // ✅ 보전일시자금 전용 — 지점명/이체금액이 바뀔 때마다 처리내용/이체방법 재조립 (touched 필드는 보호)
  useEffect(() => {
    if (detailOption !== "fund_transfer") return;
    const amt = amount ? Number(amount).toLocaleString() : "0";
    const detail = `${vendorInput} 통장 잔액 부족분 충당을 위하여 용역수익금 통장에서 ${vendorInput} 통장으로 보전일시자금 ${amt}원 이체. 보전일시자금은 추후 선납세액 환급 등으로 ${vendorInput} 통장 잔액 확보 시 법인통장으로 재이체 예정.`;
    const method = `용역수익금 통장에서 ${vendorInput} 통장으로 계좌이체`;

    setTemplateInputs((prev) => ({
      ...prev,
      detail: touchedFields.detail ? prev.detail : detail,
      method: touchedFields.method ? prev.method : method,
    }));
  }, [detailOption, vendorInput, amount, touchedFields.detail, touchedFields.method]);

  // ✅ 복지포인트 전용 — 거래처(vendorInput)로 신청인 자동 채움, 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "welfare_points") return;
    if (touchedFields.welfareApplicant) return;
    if (!vendorInput) return;
    setTemplateField("welfareApplicant", vendorInput);
  }, [detailOption, vendorInput, touchedFields.welfareApplicant]);

  // ✅ 용역 물품구매 전용 — 불러온 원본의 구분(service_type)에서 "(용역)" 등 괄호 접미사를 제거해 대상 자동 채움,
  // 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "service_purchase") return;
    if (touchedFields.purchaseTarget) return;
    if (!loadedServiceType) return;
    setTemplateField("purchaseTarget", loadedServiceType.replace(/\([^)]*\)/g, "").trim());
  }, [detailOption, loadedServiceType, touchedFields.purchaseTarget]);

  // ✅ 용역 물품구매 전용 — 불러온 원본의 품목 내역(items)으로 상품 행 자동 구성
  // (할인/배송비는 원본에 없는 개념이라 빈값, 합계/소계는 기존 자동계산이 처리)
  // 사용자가 직접 행을 수정/추가/삭제했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "service_purchase") return;
    if (touchedFields.purchaseRows) return;
    if (!loadedServiceItems.length) return;
    setTemplateField(
      "purchaseRows",
      loadedServiceItems.map((it) => ({
        name: it.name || "",
        qty: String(it.qty ?? ""),
        price: String(it.price ?? ""),
        discount: "",
        shipping: "",
        note: it.remark || "",
      }))
    );
  }, [detailOption, loadedServiceItems, touchedFields.purchaseRows]);

  // ✅ 위탁수수료(보건/안전) 전용 — 원본의 집행일(execution_date) 기반 월을 귀속연월 기본값으로 자동 채움,
  // 없으면 빈 값(수동 입력). 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "service_commission_health" && detailOption !== "service_commission_safety") return;
    if (touchedFields.commissionMonth) return;
    if (!executionMonth) return;
    setTemplateField("commissionMonth", executionMonth);
  }, [detailOption, executionMonth, touchedFields.commissionMonth]);

  // ✅ 위탁수수료(보건/안전) 전용 — 위탁업체 기본값 자동 채움, 이후 수정 가능(업체가 바뀔 수 있음)
  useEffect(() => {
    if (detailOption !== "service_commission_health" && detailOption !== "service_commission_safety") return;
    if (touchedFields.commissionVendor) return;
    setTemplateField(
      "commissionVendor",
      detailOption === "service_commission_health"
        ? "소중한 산업보건연구소(주)"
        : "(사)대한산업안전협회 서울지역본부"
    );
  }, [detailOption, touchedFields.commissionVendor]);

  // ✅ 카페인 가산점/독산점/금나래점 신용카드결제 전용 — 카드번호 기본값을 점포별로 채움(같은
  // templateState.gasanCardNo를 세 detailOption이 공유하므로, service_commission_health/safety의
  // commissionVendor와 동일한 패턴으로 분기). 이후 수정 가능(카드가 바뀔 수 있음)
  useEffect(() => {
    if (
      detailOption !== "gasan_credit_card" &&
      detailOption !== "doksan_credit_card" &&
      detailOption !== "geumnarae_credit_card"
    )
      return;
    if (touchedFields.gasanCardNo) return;
    setTemplateField(
      "gasanCardNo",
      detailOption === "doksan_credit_card" || detailOption === "geumnarae_credit_card"
        ? "4340, 8727"
        : "4261, 0887"
    );
  }, [detailOption, touchedFields.gasanCardNo]);

  // ✅ 카페인 금나래점 신용카드결제 전용 — 비고 기본값(실제 이체일 "매월 1일 이체", 가산/독산은 초기값
  // "매월 23일 이체"를 그대로 사용하므로 금나래만 별도 분기)
  useEffect(() => {
    if (detailOption !== "geumnarae_credit_card") return;
    if (touchedFields.gasanCardNote) return;
    setTemplateField("gasanCardNote", "매월 1일 이체");
  }, [detailOption, touchedFields.gasanCardNote]);

  // ✅ 일용직급여 전용 — 원본의 집행일(execution_date) 기반 월을 귀속연월 기본값으로 자동 채움,
  // 없으면 빈 값(수동 입력). 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "service_daily_wage") return;
    if (touchedFields.dailyWageMonth) return;
    if (!executionMonth) return;
    setTemplateField("dailyWageMonth", executionMonth);
  }, [detailOption, executionMonth, touchedFields.dailyWageMonth]);

  // ✅ 카페인 가산점 전용 — 원본의 집행일(execution_date) 기반 월을 귀속연월 기본값으로 자동 채움,
  // 없으면 빈 값(수동 입력). 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (!GASAN_MONTH_TEMPLATES.includes(detailOption)) return;
    if (touchedFields.gasanMonth) return;
    if (!executionMonth) return;
    setTemplateField("gasanMonth", executionMonth);
  }, [detailOption, executionMonth, touchedFields.gasanMonth]);

  // ✅ 일용직급여 전용 — 불러온 원본의 인건비 내역(salaries)으로 행 자동 구성
  // (근무자←name, 지급←totalSalary, 공제←incomeTax+localTax+health+care+pension+employment 합산,
  //  근무지/근무일자는 원본에 없는 개념이라 매번 수동 입력, 실지급은 기존 자동계산이 처리)
  // 사용자가 직접 행을 수정/추가/삭제했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "service_daily_wage") return;
    if (touchedFields.dailyWageRows) return;
    if (!loadedServiceSalaries.length) return;
    setTemplateField(
      "dailyWageRows",
      loadedServiceSalaries.map((s) => ({
        name: s.name || "",
        site: "",
        workDate: "",
        pay: String(s.totalSalary ?? ""),
        deduction: String(
          (Number(s.incomeTax) || 0) +
            (Number(s.localTax) || 0) +
            (Number(s.health) || 0) +
            (Number(s.care) || 0) +
            (Number(s.pension) || 0) +
            (Number(s.employment) || 0)
        ),
      }))
    );
  }, [detailOption, loadedServiceSalaries, touchedFields.dailyWageRows]);

  // ✅ 소모품구매 전용 — 결제방식 바뀌면 지출방법(templateInputs.method) 자동 갱신 (touched 필드는 보호)
  useEffect(() => {
    if (detailOption !== "supplies") return;
    const method = templateState.supplyPayType === "transfer" ? "계좌이체" : "신용카드결제";

    setTemplateInputs((prev) => ({
      ...prev,
      method: touchedFields.method ? prev.method : method,
    }));
  }, [detailOption, templateState.supplyPayType, touchedFields.method]);

  // ✅ 유류비 전용 — 용역사업/환경종합 카테고리에서 선택 시 예산과목 기본값을 카테고리별로 교체
  // (resetTemplateInputs가 경영팀 기본값으로 채운 직후, 카테고리/템플릿 전환 시 한 번만 덮어씀.
  //  예산과목은 TemplateFormFields에서 항상 잠금(readOnly) 처리되므로 사용자가 직접 고칠 수 없다.)
  useEffect(() => {
    if (detailOption !== "fuel_cost") return;
    if (category === "용역사업") {
      setTemplateInput(
        "budgetItem",
        "관리용역사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 차량선박비, 차량유지비"
      );
    } else if (category === "환경/종합") {
      setTemplateInput(
        "budgetItem",
        "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 차량선박비, 차량유류대"
      );
    }
  }, [detailOption, category]);

  // ✅ 유류비 전용 — 환경/종합 카테고리는 업무용 차량이 포터 1대뿐이라 차량번호/차종 기본값 자동 채움
  // (경영팀/용역사업은 차량이 여러 대라 기존대로 수동입력 유지). 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "fuel_cost" || category !== "환경/종합") return;
    if (!touchedFields.carNumber) setTemplateField("carNumber", "821로3826");
    if (!touchedFields.carModel) setTemplateField("carModel", "포터");
  }, [detailOption, category, touchedFields.carNumber, touchedFields.carModel]);

  // ✅ 차량리스료 전용 — 매달 반복되는 고정 금액(477,300원)을 소요예산 기본값으로 채움.
  // 자료를 불러와 원본 금액(loadedAmount)이 있으면 그쪽을 우선하고, 사용자가 직접 수정했으면 덮어쓰지 않음
  useEffect(() => {
    if (detailOption !== "env_total_car_lease") return;
    if (touchedFields.amount) return;
    if (loadedAmount) return;
    setAmount("477300");
  }, [detailOption, touchedFields.amount, loadedAmount]);

  // ✅ 채용심사비 전용 — 단가·회수·인원 바뀔 때마다 합계를 소요예산(amount)에 자동 반영
  useEffect(() => {
    if (detailOption !== "interview_fee") return;
    const docAmt = (Number(templateState.docFee) || 0) * (Number(templateState.docCount) || 0) * (Number(templateState.docPersons) || 0);
    const intAmt = (Number(templateState.intFee) || 0) * (Number(templateState.intCount) || 0) * (Number(templateState.intPersons) || 0);
    setAmount(String(docAmt + intAmt));
  }, [detailOption, templateState.docFee, templateState.docCount, templateState.docPersons, templateState.intFee, templateState.intCount, templateState.intPersons]);

  // ✅ 공제금환급 전용 — 대상자 행 합계를 환급금액(amount)에 자동 반영.
  // AUTO_SUM_TEMPLATES는 표가 금액의 유일한 진실이라 touched 여부와 무관하게 항상 동기화한다
  // (금액 입력칸 자체도 TemplateFormFields에서 항상 readOnly 처리됨).
  useEffect(() => {
    if (detailOption !== "deduction_refund") return;
    const total = templateState.refundRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    setAmount(String(total));
  }, [detailOption, templateState.refundRows]);

  // ✅ 퇴직연금 전용 — 부문별 행 + 사업자수수료 합계를 지출금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "pension") return;
    const deptTotal = templateState.pensionRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    setAmount(String(deptTotal + (Number(templateState.pensionFeeAmount) || 0)));
  }, [detailOption, templateState.pensionRows, templateState.pensionFeeAmount]);

  // ✅ 원천세 전용(경영팀/카페인 독산점 공용) — 행별(근로소득세+지방소득세) 총계 합산을 금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (
      detailOption !== "withholding_tax" &&
      detailOption !== "doksan_withholding_tax" &&
      detailOption !== "geumnarae_withholding_tax" &&
      detailOption !== "green_withholding_tax"
    )
      return;
    const total = templateState.withholdingRows.reduce(
      (sum, r) => sum + (Number(r.laborTax) || 0) + (Number(r.localTax) || 0),
      0
    );
    setAmount(String(total));
  }, [detailOption, templateState.withholdingRows]);

  // ✅ 카페인 독산점 명절격려품 전용 — 행별(공급가액+부가세) 소계 합산을 금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (
      detailOption !== "doksan_holiday_gift" &&
      detailOption !== "geumnarae_holiday_gift" &&
      detailOption !== "green_holiday_gift"
    )
      return;
    const total = templateState.holidayGiftRows.reduce(
      (sum, r) => sum + (Number(r.supply) || 0) + (Number(r.vat) || 0),
      0
    );
    setAmount(String(total));
  }, [detailOption, templateState.holidayGiftRows]);

  // ✅ 카페인 독산점 자판기단말기 전용 — 수량×단가(부가세 10% 반올림, ExpenseItemPanel.calc()와 동일 공식)를
  // 금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "doksan_vending_terminal" && detailOption !== "green_vending_terminal") return;
    const qty = Number(templateState.vendingQty) || 0;
    const price = Number(templateState.vendingUnitPrice) || 0;
    const total = qty * price;
    setAmount(String(total));
  }, [detailOption, templateState.vendingQty, templateState.vendingUnitPrice]);

  // ✅ 정수기렌탈료 전용 — 공급가액 기준 부가세·총계를 금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "water_rental") return;
    setAmount(String(calcVatTotal(templateState.waterSupply).total));
  }, [detailOption, templateState.waterSupply]);

  // ✅ 서버이용료 전용 — 공급가액 기준 부가세·총계를 금액(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "server_fee") return;
    setAmount(String(calcVatTotal(templateState.serverSupply).total));
  }, [detailOption, templateState.serverSupply]);

  // ✅ 용역 물품구매 전용 — 표 각 행의 소계 합산을 소요예산(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "service_purchase") return;
    const total = templateState.purchaseRows.reduce((sum, r) => sum + calcPurchaseRow(r).subtotal, 0);
    setAmount(String(total));
  }, [detailOption, templateState.purchaseRows]);

  // ✅ 용역 일용직급여 전용 — 표 각 행의 지급 합산(실지급 아님)을 소요예산(amount)에 항상 자동 반영
  useEffect(() => {
    if (detailOption !== "service_daily_wage") return;
    const total = templateState.dailyWageRows.reduce((sum, r) => sum + calcDailyWageRow(r).pay, 0);
    setAmount(String(total));
  }, [detailOption, templateState.dailyWageRows]);

  // ✅ 카페인 가산점/독산점/금나래점/그린점 포스임대료 전용 — 공급가액 기준 부가세·총계를 금액(amount)에
  // 항상 자동 반영 (독산점 분은 이전 작업에서 조건 추가가 누락되어 있던 것을 금나래 작업 중 함께 보강)
  useEffect(() => {
    if (
      detailOption !== "gasan_pos_rental" &&
      detailOption !== "doksan_pos_rental" &&
      detailOption !== "geumnarae_pos_rental" &&
      detailOption !== "green_pos_rental"
    )
      return;
    setAmount(String(calcVatTotal(templateState.posSupply).total));
  }, [detailOption, templateState.posSupply]);

  // ✅ 카페인 금나래점 포스임대료 전용 — 공급가액/비고 기본값(실제 청구 내역 고정값: 100,000원/매월 5일 자동이체)
  useEffect(() => {
    if (detailOption !== "geumnarae_pos_rental") return;
    if (touchedFields.posSupply) return;
    setTemplateField("posSupply", "100000");
    setTemplateField("posNote", "매월 5일 자동이체");
  }, [detailOption, touchedFields.posSupply]);

  // ✅ 카페인 가산점/독산점/금나래점/그린점 사회보험료 전용 — 직원별 본인+기관부담금 총계 합산을
  // 지출금액(amount)에 항상 자동 반영 (독산점 분은 이전 작업에서 조건 추가가 누락되어 있던 것을
  // 금나래 작업 중 함께 보강)
  useEffect(() => {
    if (
      detailOption !== "gasan_social_insurance" &&
      detailOption !== "doksan_social_insurance" &&
      detailOption !== "geumnarae_social_insurance" &&
      detailOption !== "green_social_insurance"
    )
      return;
    const total = templateState.socialInsuranceRows.reduce((sum, r) => {
      const { selfTotal, orgTotal } = calcSocialInsuranceRow(r);
      return sum + selfTotal + orgTotal;
    }, 0);
    setAmount(String(total));
  }, [detailOption, templateState.socialInsuranceRows]);


  // ✅ 지출 세부유형(템플릿) 선택 — touched 아닌 입력 필드만 템플릿 기본값으로 채움
  const handleDetailOptionChange = (value) => {
    const isRealChange = value !== detailOption;
    setDetailOption(value);
    if (!isRealChange) return;

    resetTemplateInputs(value);

    // ✅ DB에서 불러온 원본 금액 보호 — 자동합계 템플릿이 아닌 곳으로 이동하면 원본 금액 복원
    if (!AUTO_SUM_TEMPLATES.includes(value) && loadedAmount) {
      setAmount(loadedAmount);
    }

    // ✅ 법인인감증명서 전용 — 자료를 먼저 불러오고 나중에 버튼을 누른 경우, 저장해둔 loadedItems로 조립
    if (value === "corporate_seal") {
      const detailText = assembleSealDetail(loadedItems);
      if (detailText) {
        setTemplateInputs((prev) => ({ ...prev, detail: detailText }));
      }
    }
  };

  // ✅ 결재자 선택
  const handleSelectApprovers = (selectedUsers) => {
    console.log("✅ 선택된 결재자:", selectedUsers);
    setApprovers(selectedUsers);
  };

  // ✅ 저장 기능
  // ✅ 폼 제출 시에는 검증만 하고 상신 직전 결재라인 재확인 다이얼로그를 띄운다 —
  // 실제 저장은 다이얼로그에서 "상신"을 눌러야(doSubmit) 진행된다. 취소 시 작성
  // 화면 그대로 유지(저장 안 함).
  const onSubmit = (e) => {
    e.preventDefault();
    if (saving) return;

    const isExpense = documentType.startsWith("expense");
    if ((!isExpense && !subcategory) || !title || !amount || !vendorInput) {
      alert("필수 항목을 모두 입력해 주세요.");
      return;
    }

    if (approvers.length === 0) {
      alert("결재선을 등록해주세요.\n(가운데 패널의 '수정' 버튼을 눌러 결재자를 추가하세요.)");
      return;
    }

    setShowApprovalConfirm(true);
  };

  const doSubmit = async () => {
    setShowApprovalConfirm(false);
    setSaving(true);

    const isExpense = documentType.startsWith("expense");

    let safeWorkMonth = workMonth;
    if (safeWorkMonth.includes("년")) {
      safeWorkMonth = safeWorkMonth.replace("년 ", "-").replace("월", "").trim();
    }

    // ✅ 결재선 데이터 변환
    const approval_lines_data = approvers.map((approver, idx) => ({
      approver_id: approver.id,
      approval_type: approver.status || "결재",
      order: idx + 1,
      position: approver.position || "",
    }));

    const payload = {
      document_type: documentType,
      category,
      subcategory,
      detail_option: detailOption,                                              
      title,
      draft_date: draftDate,

      claim_date: claimDate || null,
      work_month: safeWorkMonth,
      // ✅ 방어 처리 — vendorId는 위 두 지점에서 항상 pk로 세팅되지만, 혹시 모를
      // 경로 누락에 대비해 저장 시점에도 한 번 더 dict→id 추출(2중 안전장치).
      partner: vendorId?.id ?? vendorId ?? null,
      partner_name: vendorInput,
      amount: amount ? Number(amount) : 0,
      pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      desc: desc || "",
      attachment_items: attachments                                         
        .filter(att => att.name.trim() !== "")
        .map(att => ({ name: att.name, count: att.count })),
      seq,
      draft_no: draftNo,
      approval_lines_data,  // ✅ 결재선 포함
      
      source_app: endpointToApp(sourceEndpoint),  // ✅ 추가
      source_id: sourceItemId || null,             // ✅ 추가

      // ✅ 용역 수입기안 청구형 ↔ 기성계 연결 — tableType이 yongyeok_claim일 때만 의미
      // 있음(다른 문서 흐름에서 templateState에 남아있을 수 있는 값이 새어들지 않도록 가드).
      gisung_record: tableType === "yongyeok_claim" ? (templateState.yongyeokGisungRecordId || null) : null,

      // ✅ 리팩터링 ② — 템플릿 입력값 구조적 저장 (검색·집계 목적), 지출 템플릿에만 의미 있음.
      // 용역 수입기안 청구형/제출형(공단 계약)도 내부/공문 draft_no 등 보존을 위해 저장 —
      // template_code는 사업장 키(siteKey)로, 목록 2행 전개(DraftDocumentList.jsx)의 판별용
      template_code: isExpense
        ? detailOption
        : (["yongyeok_claim", "yongyeok_submit"].includes(tableType) ? (VENDOR_CONFIG[vendorInput]?.siteKey || "") : ""),
      template_data: isExpense || ["yongyeok_claim", "yongyeok_submit"].includes(tableType) ? templateState : null,
    };

    try {
      const { data } = await api.post(`/draft/documents/`, payload);

      // ✅ 디버그 추가
      console.log("저장 응답:", data);  // id가 있는지 확인
      await api.post(`/draft/documents/${data.id}/submit/`);

      // ✅ 원본 수입/지출 데이터 is_drafted 처리는 서버(DraftDocumentCreateSerializer.create())로
      // 이전됨 — source_app/source_id가 있으면 서버가 알아서 원본 레코드를 갱신한다.


      setShowSaveSuccessModal(true);  // ✅ 저장 완료 모달 표시

      try {
        const seqRes = await api.get(`/draft/documents/next-seq/?date=${draftDate}&category=${category}&document_type=${documentType}`);
        setSeq(String(seqRes.data.next_seq).padStart(3, "0"));
      } catch {}


    } catch (err) {

      console.error("❌ 저장 실패:", err.response?.data || err);
      // 기안번호 중복만 원인을 알려준다(나머지는 기존 문구 유지)
      alert(err.response?.data?.draft_no ? formatDraftSaveError(err) : "서버 저장 실패. 관리자에게 문의하세요.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ 미리보기 PDF 다운로드 (저장 완료 모달에서 사용)
  const handleDownloadPDF = () => {
    const element = document.querySelector('.preview-paper');
    if (!element) return alert("미리보기를 먼저 확인해주세요.");

    const originalHeight = element.style.height;
    element.style.height = '297mm';
    element.style.minHeight = '297mm';
    element.style.maxHeight = '297mm';

    const opt = {
      margin: 0,
      filename: `${title || '기안서'}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
      },
      jsPDF: {
        unit: 'px',
        format: [794, 1123],
        orientation: 'portrait',
        hotfixes: ['px_scaling'],
      },
    };

    import('html2pdf.js').then(({ default: html2pdf }) => {
      html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => { element.style.height = originalHeight; })
        .catch(() => { element.style.height = originalHeight; });
    });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.split}>
        
        {/* 왼쪽: 입력 폼 (TemplateFormFields.jsx로 분리 — 리팩터링 2단계) */}
        <TemplateFormFields
          isDataLoaded={isDataLoaded}
          draftNo={draftNo}
          title={title}
          sourceEndpoint={sourceEndpoint}
          sourceItemId={sourceItemId}
          documentType={documentType}
          category={category}
          subcategory={subcategory}
          draftDate={draftDate}
          claimDate={claimDate}
          workMonth={workMonth}
          vendorInput={vendorInput}
          handleChangeVendor={handleChangeVendor}
          handleVendorKeyDown={handleVendorKeyDown}
          showVendorList={showVendorList}
          filteredVendors={filteredVendors}
          highlightIndex={highlightIndex}
          handlePickVendor={handlePickVendor}
          amount={amount}
          setAmount={setAmount}
          setTouchedFields={setTouchedFields}
          payMethodLabel={payMethodLabel}
          serviceContent={serviceContent}
          setServiceContent={setServiceContent}
          templateState={templateState}
          setTemplateField={setTemplateField}
          detailOption={detailOption}
          templateInputs={templateInputs}
          setTemplateInput={setTemplateInput}
          attachments={attachments}
          setAttachments={setAttachments}
          desc={desc}
          setDesc={setDesc}
          setTitle={setTitle}
          setTableType={setTableType}
          tableType={tableType}
          onSubmit={onSubmit}
          setShowLoadModal={setShowLoadModal}
          showYongyeokGisungModal={showYongyeokGisungModal}
          setShowYongyeokGisungModal={setShowYongyeokGisungModal}
          yongyeokGisungList={yongyeokGisungList}
          yongyeokGisungLoading={yongyeokGisungLoading}
          fetchYongyeokGisungList={fetchYongyeokGisungList}
          handlePickYongyeokGisung={handlePickYongyeokGisung}
          yongyeokGisungYear={yongyeokGisungYear}
          setYongyeokGisungYear={setYongyeokGisungYear}
          yongyeokGisungMonth={yongyeokGisungMonth}
          setYongyeokGisungMonth={setYongyeokGisungMonth}
          yongyeokGisungDraftedFilter={yongyeokGisungDraftedFilter}
          setYongyeokGisungDraftedFilter={setYongyeokGisungDraftedFilter}
        />
        
        {/* ✅ 가운데: 문서종류 선택 + 결재라인 (세로 배치) */}
        <div className={styles.middlePane}>
          {/* ✅ 자료를 불러온 경우에만 문서종류 선택 표시 */}
          {isDataLoaded && (
            <div className={styles.documentTypeSection}>
              <DocumentTypeSelector
                documentType={documentType}
                onDocumentTypeChange={setDocumentType}
                category={category}
                onCategoryChange={setCategory}
                detailOption={detailOption}
                onDetailOptionChange={handleDetailOptionChange}
                isDataLoaded={isDataLoaded}  // ✅ 추가
                hideDocumentType={!!fixedDocumentType}
              />
            </div>
          )}
          
          <ApprovalLine
            approvers={approvers}
            setApprovers={setApprovers}
            onOpenModal={() => setShowModal(true)}
            documentType={documentType}
            disableAutoDefault={!!id}
          />
        </div>

        {/* 오른쪽: 미리보기 (DocumentPreview.jsx로 분리 — 리팩터링 1단계) */}
        <DocumentPreview
          documentType={documentType}
          detailOption={detailOption}
          category={category}
          subcategory={subcategory}
          templateState={templateState}
          templateInputs={templateInputs}
          title={title}
          amount={amount}
          vendorInput={vendorInput}
          serviceContent={serviceContent}
          workMonth={workMonth}
          executionMonth={executionMonth}
          payMethodLabel={payMethodLabel}
          claimDate={claimDate}
          desc={desc}
          attachments={attachments}
          aircondDetails={aircondDetails}
          tableType={tableType}
          draftNo={draftNo}
          approvers={approvers}
          onSave={onSubmit}
          saving={saving}
          onYongyeokViewChange={(view) => setTemplateField("claimActiveView", view)}
        />


      </div>
  
      {/* ✅ 불러오기 모달 */}
      {showLoadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowLoadModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>📥 수입/지출 자료 불러오기 (데이터 입력은 했지만 기안서 미작성 자료입니다)</h3>
              <button 
                type="button" 
                onClick={() => setShowLoadModal(false)}
                className={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* ✅ 필터들을 한 줄로 묶기 */}
              <div className={styles.filterRow}>
                
                {/* 날짜 필터 */}
                <div className={styles.dateFilterCompact}>
                  <label>기간:</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={styles.dateInput} />
                  <span>~</span>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={styles.dateInput} />
                </div>

                {/* 문서 종류 선택 — fixedDocumentType으로 고정된 페이지(예: 지출기안 전용)에서는 숨김 */}
                {!fixedDocumentType && (
                  <div className={styles.documentTypeFilterCompact}>
                    <label>문서:</label>
                    <div className={styles.inline}>
                      {[
                        { value: "income_draft", label: "수입기안" },
                        { value: "expense_draft", label: "지출기안" },
                        { value: "income_resolution", label: "수입결의" },
                        { value: "expense_resolution", label: "지출결의" },
                      ].map((type) => (
                        <label key={type.value} className={styles.radio}>
                          <input
                            type="radio"
                            name="documentType"
                            value={type.value}
                            checked={documentType === type.value}
                            onChange={(e) => setDocumentType(e.target.value)}
                          />
                          {type.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* 카테고리 */}
                <div className={styles.loadControlsCompact}>
                  <label>카테고리:</label>
                  <select value={loadCategory} onChange={(e) => setLoadCategory(e.target.value)} className={styles.categorySelect}>
                    {CATEGORY_OPTIONS[documentType].map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setLoadSearchTerm("");
                      fetchIncomeList();
                    }}
                    className={styles.refreshBtn}
                  >
                    🔄 새로고침
                  </button>

                </div>

                {/* 제목·거래처 검색 */}
                <div className={styles.searchFilterCompact}>
                  <input
                    type="text"
                    value={loadSearchTerm}
                    onChange={(e) => setLoadSearchTerm(e.target.value)}
                    placeholder="🔍 제목·거래처 검색"
                    className={styles.searchFilterInput}
                  />
                </div>

              </div>

              {/* ✅ 미작성만 보기 토글 — 기본 켜짐, 끄면 이미 기안 작성된 건도 표시/선택 가능 */}
              <div className={styles.listToggleRow}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={showUndraftedOnly}
                    onChange={(e) => setShowUndraftedOnly(e.target.checked)}
                  />
                  미작성만 보기
                </label>
              </div>

              {loadingList ? (
                <div className={styles.loadingText}>불러오는 중...</div>
              ) : incomeList.length === 0 ? (
                <div className={styles.emptyText}>
                  불러온 자료가 없습니다.
                </div>
              ) : filteredIncomeList.length === 0 ? (
                <div className={styles.emptyText}>
                  조건에 맞는 자료가 없습니다.
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>작성일</th>
                        <th>기안번호</th>
                        <th>상태</th>
                        <th>제목</th>
                        <th>거래처</th>
                        <th>금액</th>
                        <th>선택</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIncomeList.map((item) => (
                        <tr key={item.id}>
                          <td>{item.draft_date}</td>
                          <td>{item.draft_no || "-"}</td>
                          <td>
                            <span
                              className={
                                item.is_drafted
                                  ? styles.statusBadgeDone
                                  : styles.statusBadgeUndone
                              }
                            >
                              {item.is_drafted ? "작성완료" : "미작성"}
                            </span>
                          </td>
                          <td>{item.title}</td>
                          <td>{item.partner_name}</td>

                          <td style={{ textAlign: "right" }}>
                            {resolveItemAmount(item).toLocaleString()}원
                          </td>

                          <td>
                            <button
                              type="button"
                              onClick={() => handleSelectData(item)}
                              className={styles.selectBtn}
                            >
                              선택
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
  
      {/* 결재자 선택 모달 */}
      {showModal && (
        <ApprovalLineModal
          onClose={() => setShowModal(false)}
          onSelect={handleSelectApprovers}
          documentType={documentType}
          initialApprovers={approvers}
        />
      )}

      {showApprovalConfirm && (
        <ApprovalLineConfirmDialog
          approvers={approvers}
          onCancel={() => setShowApprovalConfirm(false)}
          onConfirm={doSubmit}
        />
      )}

      {/* ✅ 저장 완료 모달 */}
      {showSaveSuccessModal && (
        <div className={styles.modalOverlay} onClick={() => setShowSaveSuccessModal(false)}>
          <div 
            className={styles.modalContent} 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '480px', textAlign: 'center', padding: '40px 30px' }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>수입기안이 저장되었습니다</h3>
            <div style={{ color: '#555', fontSize: '14px', marginBottom: '8px' }}>
              문서번호: {draftNo}
            </div>
            <div style={{ color: '#555', fontSize: '14px', marginBottom: '8px' }}>
              거래처: {vendorInput}
            </div>
            <div style={{ color: '#555', fontSize: '14px', marginBottom: '24px' }}>
              금액: {amount ? Number(amount).toLocaleString() : 0}원
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleDownloadPDF}
                style={{
                  padding: '10px 24px',
                  background: '#3498db',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                📥 PDF 다운로드
              </button>
              <button
                type="button"

                onClick={() => {
                  setShowSaveSuccessModal(false);
                  setSourceDraftNo("");  // ✅ 추가

                  setIsDataLoaded(false);
                  setVendorInput("");
                  setVendorId(null);
                  setAmount("");
                  setDesc("");
                  setTitle("");
                  setSubcategory("");
                  setWorkMonth("");
                  setDetailOption("");
                  setApprovers([]);
                  setAttachments([{ id: 1, name: "", count: 1 }]);
                  setSourceItemId(null);
                  setSourceEndpoint(null);
                  setAircondDetails([]);  // ✅ [추가]
                  setTableType(null);  // ✅ 추가



                }}



                style={{
                  padding: '10px 24px',
                  background: '#95a5a6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ Row 컴포넌트 - inline prop 추가
export function Row({ label, children, inline = false }) {
  return (
    <div className={inline ? styles.rowInline : styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}


// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ✅ "YYYY-MM-DD" → "YYYY년 M월" (기장수수료/임직원급여 전용 — execution_date/payDateRaw에서 월 추출)
export function formatYearMonth(dateStr) {
  if (!dateStr) return "";
  const [year, month] = dateStr.split("-");
  if (!year || !month) return "";
  return `${year}년 ${parseInt(month, 10)}월`;
}

// ✅ 인터넷·전화요금 전용 — 품목명("2026년 4월 본사 인터넷...") 맨 앞의 "YYYY년 M월"만 추출
export function extractYearMonth(text) {
  if (!text) return "";
  const m = text.match(/^(\d{4})년\s*(\d{1,2})월/);
  return m ? `${m[1]}년 ${m[2]}월` : "";
}

// ✅ 납부월류 입력 정규화 공용 파서 — "2026 5"/"2026-5"/"2026.5"/"202605"/"26 5"/
// "7월"/"7" 등 다양한 표기에서 {year, month}를 최대한 관대하게 뽑아낸다. 연도가
// 없는 입력("7"/"7월")은 year:null로 반환 — 연도가 꼭 필요한 표기(YYYY년 M월)에는
// 못 쓰지만, 연도 없이 "M월"만 쓰는 표기에는 그대로 유효하다. 해석 불가능하면 null.
export function parseFlexibleYearMonth(raw) {
  if (!raw) return null;
  const s = raw.trim();
  let m;
  if ((m = s.match(/^(\d{4})년\s*(\d{1,2})월$/))) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
  if ((m = s.match(/^(\d{1,2})월$/))) return { year: null, month: parseInt(m[1], 10) };
  if ((m = s.match(/^(\d{4})(\d{2})$/))) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
  if ((m = s.match(/^(\d{2}|\d{4})[\s\-.]+(\d{1,2})$/))) {
    const year = m[1].length === 2 ? 2000 + parseInt(m[1], 10) : parseInt(m[1], 10);
    return { year, month: parseInt(m[2], 10) };
  }
  if ((m = s.match(/^(\d{1,2})$/))) return { year: null, month: parseInt(m[1], 10) };
  return null;
}

function isValidMonth(n) {
  return n >= 1 && n <= 12;
}

// ✅ 납부월(연도 필수) 정규화 — utility_fee/credit_card/accounting_fee처럼 도입문·
// 제목에 "YYYY년 M월" 전체 표기가 필요한 템플릿 전용. "2026 5"/"2026-5"/"2026.5"/
// "202605"/"26 5"/"2026년 5월" 전부 "2026년 5월"로. 연도 정보가 없는 입력(예:
// "7")은 해석 불가로 보고 원본 그대로 둔다(잘못된 연도를 추측해 끼워넣지 않음).
export function normalizeYearMonth(raw) {
  const parsed = parseFlexibleYearMonth(raw);
  if (!parsed || parsed.year == null || !isValidMonth(parsed.month)) return raw;
  return `${parsed.year}년 ${parsed.month}월`;
}

// ✅ "M월" 단독 표기 정규화 — 사무실관리비/전기료/퇴직연금/정수기처럼 연도 없이
// "M월"만 쓰는 템플릿 전용. 연도가 섞여 들어와도(예: "2026년 7월") 월만 뽑아
// "7월"로, 연도 없이 월만 입력해도("7"/"7월") 그대로 정규화된다.
export function normalizeMonthOnly(raw) {
  const parsed = parseFlexibleYearMonth(raw);
  if (!parsed || !isValidMonth(parsed.month)) return raw;
  return `${parsed.month}월`;
}

// ✅ 납기일류 입력 정규화 — "2026 7 31"/"2026-7-31"/"2026.7.31"/"20260731" 등을
// 구 페이지 원본 PDF 표기와 동일한 "YYYY-MM-DD"로. 해석 불가능한 입력은 원본
// 그대로 사용(예: 아직 다 안 친 중간 상태, 자유 텍스트 비고 등).
export function normalizeDateInput(raw) {
  if (!raw) return raw;
  const s = raw.trim();
  const pad = (n) => String(n).padStart(2, "0");
  const isValidDate = (mo, d) => mo >= 1 && mo <= 12 && d >= 1 && d <= 31;

  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) {
    const month = parseInt(m[2], 10), day = parseInt(m[3], 10);
    return isValidDate(month, day) ? `${m[1]}-${pad(month)}-${pad(day)}` : raw;
  }

  m = s.match(/^(\d{4})[\s\-.]+(\d{1,2})[\s\-.]+(\d{1,2})$/);
  if (m) {
    const month = parseInt(m[2], 10), day = parseInt(m[3], 10);
    return isValidDate(month, day) ? `${m[1]}-${pad(month)}-${pad(day)}` : raw;
  }

  return raw;
}

// ✅ 납기일류 입력에서 {year, month, day}만 뽑아내는 파서 — normalizeDateInput과 같은
// 표기(들)를 인식하지만 최종 문자열이 아니라 분리 입력칸(YearMonthDayFields) 초기화/
// 외부 동기화 전용으로 값을 그대로 반환한다. 해석 불가능하면 null.
export function parseFlexibleDate(raw) {
  if (!raw) return null;
  const s = raw.trim();
  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10), day: parseInt(m[3], 10) };
  m = s.match(/^(\d{4})[\s\-.]+(\d{1,2})[\s\-.]+(\d{1,2})$/);
  if (m) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10), day: parseInt(m[3], 10) };
  return null;
}

// ✅ 납부월류 분리 입력 — 연(4자리, 기본값 올해)+월 두 칸. showYear=false면 연도 칸을
// 아예 숨기고 "M월"만 조립(사무실관리비/전기료/퇴직연금/정수기 — 연도 없는 원본 표기
// 그대로). value/onChange는 항상 조립된 표시 문자열("2026년 5월" 또는 "5월") 하나로
// 주고받는다 — 제목·도입문·미리보기는 이 문자열을 기존 경로 그대로 사용(회귀 없음).
// 외부에서 값이 바뀔 때만(자료 불러오기 등) 입력칸을 재동기화하고, 스스로 emit한
// 값이면 재동기화를 건너뛰어 타이핑 중 재포맷으로 커서가 튀는 걸 막는다.
export function YearMonthFields({ label, value, onChange, showYear = true }) {
  const lastEmitted = useRef(value);
  const initial = parseFlexibleYearMonth(value) || {};
  const [year, setYear] = useState(String(initial.year ?? (showYear ? new Date().getFullYear() : "")));
  const [month, setMonth] = useState(initial.month != null ? String(initial.month) : "");

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parseFlexibleYearMonth(value) || {};
    setYear(String(parsed.year ?? (showYear ? new Date().getFullYear() : "")));
    setMonth(parsed.month != null ? String(parsed.month) : "");
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextYear, nextMonth) => {
    // ✅ 입력칸엔 사용자가 친 그대로("05") 두고, 조립 문자열만 앞자리 0을 정리(parseInt)해
    // "5월"로 — 입력칸 재포맷은 타이핑을 방해하지만 조립 결과 포맷은 상관없다.
    const m = parseInt(nextMonth, 10);
    const assembled = !m ? "" : showYear ? `${nextYear || new Date().getFullYear()}년 ${m}월` : `${m}월`;
    lastEmitted.current = assembled;
    onChange(assembled);
  };

  return (
    <Row label={label}>
      <div className={styles.dateSplitRow}>
        {showYear && (
          <>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={year}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, "");
                setYear(v);
                emit(v, month);
              }}
              className={styles.dateSplitYear}
            />
            <span>년</span>
          </>
        )}
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={month}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, "");
            setMonth(v);
            emit(year, v);
          }}
        />
        <span>월</span>
      </div>
    </Row>
  );
}

// ✅ 납기일류 분리 입력 — 연(기본값 올해)+월+일 세 칸 → "YYYY-MM-DD" 조립(구 페이지
// 원본 PDF 표기와 동일). 재동기화/타이핑 방해 방지 원칙은 YearMonthFields와 동일.
export function DueDateFields({ label, value, onChange }) {
  const lastEmitted = useRef(value);
  const initial = parseFlexibleDate(value) || {};
  const [year, setYear] = useState(String(initial.year ?? new Date().getFullYear()));
  const [month, setMonth] = useState(initial.month != null ? String(initial.month) : "");
  const [day, setDay] = useState(initial.day != null ? String(initial.day) : "");

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parseFlexibleDate(value) || {};
    setYear(String(parsed.year ?? new Date().getFullYear()));
    setMonth(parsed.month != null ? String(parsed.month) : "");
    setDay(parsed.day != null ? String(parsed.day) : "");
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextYear, nextMonth, nextDay) => {
    const pad = (n) => String(n).padStart(2, "0");
    const assembled = !nextMonth || !nextDay ? "" : `${nextYear || new Date().getFullYear()}-${pad(nextMonth)}-${pad(nextDay)}`;
    lastEmitted.current = assembled;
    onChange(assembled);
  };

  return (
    <Row label={label}>
      <div className={styles.dateSplitRow}>
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={year}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, "");
            setYear(v);
            emit(v, month, day);
          }}
          className={styles.dateSplitYear}
        />
        <span>년</span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={month}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, "");
            setMonth(v);
            emit(year, v, day);
          }}
        />
        <span>월</span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={day}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, "");
            setDay(v);
            emit(year, month, v);
          }}
        />
        <span>일</span>
      </div>
    </Row>
  );
}

// ✅ 법인인감증명서 전용 — items 배열을 "품목명 수량부*단가원=금액원" 줄바꿈 조립
function assembleSealDetail(items) {
  return (items || [])
    .map(
      (it) =>
        `${it.name} ${it.qty}부*${Number(it.price).toLocaleString()}원=${Number(it.total).toLocaleString()}원`
    )
    .join("\n");
}

// ✅ 리팩터링 1단계 — templateState 초기값/리셋값 (배치 단위로 키 추가 중)
// 배열/객체 기본값이 여러 인스턴스에서 공유되지 않도록 매번 새 객체를 반환하는 함수로 정의.
// 아직 이전 안 된 템플릿은 기존 개별 useState를 그대로 사용 (마이그레이션 진행 중).
function getInitialTemplateState() {
  return {
    // 배치1
    parkingBranch: "",
    parkingReason: "",
    sealReason: "",
    bizTarget: "",
    bizCategory: "",
    payDateRaw: "",
    maintMonth: "",
    maintDueDateRaw: "",
    // ✅ 납부월류 분리 입력 확장 — utility_fee/credit_card/accounting_fee 전용 신규 필드
    utilityMonth: "",
    creditCardMonth: "",
    accountingMonth: "",
    // ✅ 인터넷·전화요금 납기일 — templateInputs가 아니라 여기 두는 이유: template_data로
    // 저장/복원되는 건 templateState뿐(templateInputs는 세션 한정, 저장 안 됨). 이름은
    // 카페 수도/가스/전기의 utilityDueRaw(그룹 공용)와 겹치지 않도록 별도로 둔다.
    utilityFeeDueDateRaw: "",
    // 배치2
    donationReason: "",
    donationTarget: "",
    welfareApplicant: "",
    welfareReason: "",
    contractName: "",
    contractAmountRaw: "",
    contractStartRaw: "",
    contractEndRaw: "",
    trainee: "",
    trainCourse: "",
    trainSchedule: "",
    trainTarget: "",
    trainInstitution: "",
    // 배치3
    adTarget: "",
    elecMonth: "",
    elecUsageStartRaw: "",
    elecUsageEndRaw: "",
    elecDueDateRaw: "",
    carNumber: "",
    carModel: "",
    supplyPayType: "card",
    // 배치4
    docFee: "100000",
    docCount: "1",
    docPersons: "",
    intFee: "100000",
    intCount: "1",
    intPersons: "",
    installmentRound: "",
    refundRows: [{ name: "", amount: "" }],
    refundNote: "",
    // 배치5
    pensionMonth: "",
    pensionDueRaw: "",
    pensionRows: defaultPensionRows(),
    pensionFeeAmount: "30770",
    withholdingRows: defaultWithholdingRows(),
    withholdingDueRaw: "",
    waterMonth: "",
    waterSupply: "27000",
    waterNote: "매월 10일 계좌이체",
    serverSupply: "20000",
    serverNote: "매월 5일 자동이체",
    // 용역 물품구매/위탁수수료
    purchaseTarget: "",
    purchaseRows: defaultPurchaseRows(),
    commissionMonth: "",
    commissionVendor: "",
    dailyWageMonth: "",
    dailyWageRows: defaultDailyWageRows(),
    // 환경/종합 차량리스료
    leaseInstallmentNo: "",
    // 카페인 가산점
    gasanMonth: "",
    posSupply: "30000",
    posNote: "매월 25일 자동이체",
    gasanCardNo: "4261, 0887",
    gasanCardFee: "0",
    gasanCardNote: "매월 23일 이체",
    socialInsuranceRows: defaultSocialInsuranceRows(),
    // 카페인 독산점 전용 신규 템플릿(표 3종)
    holidayGiftRows: defaultHolidayGiftRows(),
    vendingQty: "",
    vendingUnitPrice: "",
    // 카페인 독산점 전용 신규 템플릿(납부 5종)
    vatYear: "",
    vatQuarter: "",
    vatDueRaw: "",
    hygieneYear: "",
    hygieneDueRaw: "",
    utilityUsageStartRaw: "",
    utilityUsageEndRaw: "",
    utilityDueRaw: "",
    // 카페인 금나래점 부가세 전용 — 예정고지/확정 구분(독산은 예정고지 고정이라 불필요)
    vatType: "",
    // ✅ 용역 수입기안 청구형(공단 계약, 기성계) 전용 — 내부기안/공문 2벌 + 자동계산
    claimActiveView: "internal",
    officialDraftNo: "",
    yongyeokRound: "",
    yongyeokContractAmount: "",
    yongyeokPrevAmount: "",
    yongyeokPrevCumulative: "",
    yongyeokContractName: "",
    yongyeokReferenceLabel: "",
    yongyeokRoundWarning: false,
    // ✅ 용역 수입기안 제출형(기성계 제출) 전용 — claimActiveView/officialDraftNo는 청구형과 공유
    yongyeokSubmitServiceName: "",
    yongyeokSubmitRecipient: "",
    yongyeokSubmitHonorific: "",
    yongyeokSubmitViaLabel: "",
  };
}

// ✅ 카페인 독산점 명절격려품 전용 — 지급대상/상품명/수량/공급가액/부가세 기본 1행
export function defaultHolidayGiftRows() {
  return [
    { target: "", name: "", qty: "", supply: "", vat: "" },
  ];
}

// ✅ 카페인 가산점 사회보험료 전용 — 직원별 본인/기관부담금 기본 1행
// (health/care/pension/employment는 본인·기관 각각, industrial(산재보험)은 기관부담금만 존재)
export function defaultSocialInsuranceRows() {
  return [
    {
      name: "",
      health_self: "", health_org: "",
      care_self: "", care_org: "",
      pension_self: "", pension_org: "",
      employment_self: "", employment_org: "",
      industrial_org: "",
    },
  ];
}

// ✅ 용역 물품구매 전용 — 세부내역 표 기본 1행 (상품명/수량/단가/할인/배송비/비고, 합계·소계는 자동계산)
function defaultPurchaseRows() {
  return [{ name: "", qty: "", price: "", discount: "", shipping: "", note: "" }];
}


// ✅ 용역 일용직급여 전용 — 세부내역 표 기본 1행 (근무자/근무지/근무일자/지급/공제, 실지급은 자동계산)
function defaultDailyWageRows() {
  return [{ name: "", site: "", workDate: "", pay: "", deduction: "" }];
}

// ✅ 퇴직연금 전용 — 부문별 기본 프리셋 6행 (매달 이 구성에서 값만 수정하는 흐름이라 PDF 원본값을 기본으로 채움)
function defaultPensionRows() {
  return [
    { dept: "사무직원", name: "서복성", extra: "3", amount: "1342540" },
    { dept: "환경사업", name: "강미희", extra: "0", amount: "171150" },
    { dept: "현장근로자", name: "신영례", extra: "27", amount: "8804950" },
    { dept: "카페인 가산점", name: "민영희", extra: "0", amount: "127200" },
    { dept: "카페인 독산점", name: "마시영", extra: "1", amount: "220620" },
    { dept: "카페인 금나래점", name: "이동선", extra: "0", amount: "129380" },
  ];
}

// ✅ 원천세 전용 — 기본 프리셋 1행 (PDF 원본값)
function defaultWithholdingRows() {
  return [
    { desc: "현장근로자 3월 원천징수분", name: "강미희", extra: "63", laborTax: "2420610", localTax: "243320" },
  ];
}

// ✅ 정수기렌탈료/서버이용료 공용 — 공급가×10% 부가세(반올림) + 총계
export function calcVatTotal(supply) {
  const amt = Number(supply) || 0;
  const vat = Math.round(amt * 0.1);
  return { amt, vat, total: amt + vat };
}

// ✅ 용역 물품구매 전용 — 행 하나의 합계(수량×단가-할인)/소계(합계+배송비) 계산
export function calcPurchaseRow(row) {
  const qty = Number(row.qty) || 0;
  const price = Number(row.price) || 0;
  const discount = Number(row.discount) || 0;
  const shipping = Number(row.shipping) || 0;
  const rowTotal = qty * price - discount;
  const subtotal = rowTotal + shipping;
  return { qty, price, discount, shipping, rowTotal, subtotal };
}

// ✅ 용역 일용직급여 전용 — 행 하나의 실지급(지급-공제) 계산
export function calcDailyWageRow(row) {
  const pay = Number(row.pay) || 0;
  const deduction = Number(row.deduction) || 0;
  const net = pay - deduction;
  return { pay, deduction, net };
}

// ✅ 카페인 가산점 사회보험료 전용 — 직원 1명의 본인부담금/기관부담금 합계 (산재보험은 기관부담금에만 존재)
export function calcSocialInsuranceRow(row) {
  const health_self = Number(row.health_self) || 0;
  const care_self = Number(row.care_self) || 0;
  const pension_self = Number(row.pension_self) || 0;
  const employment_self = Number(row.employment_self) || 0;
  const health_org = Number(row.health_org) || 0;
  const care_org = Number(row.care_org) || 0;
  const pension_org = Number(row.pension_org) || 0;
  const employment_org = Number(row.employment_org) || 0;
  const industrial_org = Number(row.industrial_org) || 0;
  const selfTotal = health_self + care_self + pension_self + employment_self;
  const orgTotal = health_org + care_org + pension_org + employment_org + industrial_org;
  return {
    health_self, care_self, pension_self, employment_self,
    health_org, care_org, pension_org, employment_org, industrial_org,
    selfTotal, orgTotal,
  };
}

// ✅ 임직원급여 전용 — "2026-05-22" → "2026. 5. 22. (금)"
export function formatPayDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const week = ["일", "월", "화", "수", "목", "금", "토"];
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}. (${week[d.getDay()]})`;
}

// ✅ 사무실관리비 전용 — "2026-05-26" → "2026. 5. 26." (요일 없음)
export function formatDateDots(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

// ✅ 짧은 날짜 포맷 함수 (2026-02-04 → 26.2.4.)
export function formatDateShort(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  const shortYear = year.slice(2); // 2026 → 26
  return `${shortYear}.${parseInt(month)}.${parseInt(day)}.`;
}